import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { auth, db, storage, handleFirestoreError, OperationType } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, setDoc, updateDoc, arrayUnion, arrayRemove, getDoc, collection, serverTimestamp, addDoc, query as firestoreQuery, orderBy, limit, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { LogOut, Upload, Trash2, ExternalLink, Image as ImageIcon, Loader2, Copy, Check, ShieldCheck, Clock, X, Calendar, Plus, Edit2, FileText, Download, ArrowLeft, History, Tv, Camera, Scan, Activity, AlertTriangle, AlertCircle, CheckCircle2, Share2, Monitor, Maximize, RefreshCw, MessageSquare, Volume2, VolumeX, Eye, EyeOff, HelpCircle, RotateCcw, Zap, Send, Star } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { jsPDF } from 'jspdf';
import AuraAgent from './AuraAgent';
import OnAir from './admin/OnAir';
import { QRCodeCanvas } from 'qrcode.react';
import { DEFAULT_QUOTES } from './AuraSoundscape';
import { COMMERCIAL_IMPULSES, DEFAULT_CIRCADIAN } from '../constants';

interface Schedule {
  enabled: boolean;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
  days: number[];    // [0, 1, 2, 3, 4, 5, 6]
}

interface ContentItem {
  url: string;
  name: string;
  createdAt: number;
  storagePath: string;
  schedule?: Schedule;
}

interface QuoteItem {
  category?: string;
  text: string;
  price?: string;
  tag?: string;
  ticker?: string;
  imageUrl?: string;
  schedule?: Schedule;
  showClock?: boolean;
  type?: 'user' | 'aura' | 'collaborative';
  active?: boolean;
  isImpulse?: boolean;
  isReferral?: boolean;
  expiresAt?: string;
  backupSlot?: QuoteItem | null;
}

interface TickerItem {
  text: string;
  schedule?: Schedule;
}

const SUPER_ADMIN_EMAILS = ['holasolonet@gmail.com', 'cinside.info@gmail.com'];

export default function AdminDashboard() {
  // Redirect to run.app domain if on a custom domain to bypass NGINX POST restrictions & iframe issues
  useEffect(() => {
    if (!window.location.hostname.includes('run.app') && !window.location.hostname.includes('localhost')) {
      window.location.replace(`https://prod-aura-display-3-136354148420./${window.location.pathname}${window.location.search}`);
    }
  }, []);

  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [targetUserProfile, setTargetUserProfile] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const impersonatedUid = searchParams.get('uid');
  const targetUid = impersonatedUid || user?.uid;
  const navigate = useNavigate();
  const reactLocation = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAuraStaff = useMemo(() =>
    SUPER_ADMIN_EMAILS.includes(user?.email || '') || ['admin', 'Admin', 'sales', 'Comercial'].includes(userProfile?.role),
    [user, userProfile]);

  const canManageTicker = useMemo(() =>
    userProfile?.role === 'pro' || userProfile?.role === 'total' || isAuraStaff,
    [userProfile, isAuraStaff]);

  const canAccessCollaborativeSlides = useMemo(() =>
    userProfile?.role === 'total' || isAuraStaff,
    [userProfile, isAuraStaff]);

  const isStandalone = useMemo(() =>
    window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone || document.referrer.includes('android-app://'),
    []);

  const activeTab = useMemo(() => {
    const params = new URLSearchParams(reactLocation.search);
    const paramTab = params.get('tab');
    if (paramTab === 'ia' && isAuraStaff) return 'ia';
    if (paramTab === 'mando' || paramTab === 'audio' || paramTab === 'imagen' || paramTab === 'config') return paramTab;
    return 'mando';
  }, [reactLocation.search, isAuraStaff]);

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    navigate(`${reactLocation.pathname}?${newParams.toString()}`, { replace: true });
  };

  const [contents, setContents] = useState<ContentItem[]>([]);
  const [quotes, setQuotes] = useState<QuoteItem[]>([]);
  const [merchantSlots, setMerchantSlots] = useState<QuoteItem[]>([]);
  const [auraSlots, setAuraSlots] = useState<QuoteItem[]>([]);
  const [tickers, setTickers] = useState<TickerItem[]>([]);
  const [establishmentName, setEstablishmentName] = useState('');
  const [slug, setSlug] = useState('');
  const [adminTitle, setAdminTitle] = useState('');
  const [location, setLocation] = useState('');
  const [theme, setTheme] = useState('classic');
  const [tickerTheme, setTickerTheme] = useState('classic');
  const [showTicker, setShowTicker] = useState(true);
  const [performanceMode, setPerformanceMode] = useState<'high' | 'eco'>('high');

  const [globalFeedback, setGlobalFeedback] = useState<any[]>([]);
  const [auraCredits, setAuraCredits] = useState(0);
  const [favoriteTracks, setFavoriteTracks] = useState<any[]>([]);
  const [isImpulseLoading, setIsImpulseLoading] = useState<string | null>(null);
  const [showManageFavorites, setShowManageFavorites] = useState(true);

  // Load favorites for management
  useEffect(() => {
    if (!targetUid) return;
    const favsRef = collection(db, 'clientes', targetUid, 'favorites');
    return onSnapshot(firestoreQuery(favsRef), (snap) => {
      setFavoriteTracks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [targetUid]);

  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  const paginatedFavorites = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return favoriteTracks.slice(start, start + itemsPerPage);
  }, [favoriteTracks, currentPage]);

  const totalPages = Math.ceil(favoriteTracks.length / itemsPerPage);

  // Play next track automatically
  const handleTrackEnded = () => {
    if (playingIndex !== null && playingIndex < favoriteTracks.length - 1) {
      setPlayingIndex(playingIndex + 1);
    } else {
      setPlayingIndex(null); // End of list
    }
  };

  // Stop playback when component unmounts
  useEffect(() => {
    return () => setPlayingIndex(null);
  }, []);

  const deleteFavorite = async (id: string, title: string) => {
    if (!confirm(`¿Eliminar ${title} de favoritos?`)) return;
    try {
      await deleteDoc(doc(db, 'clientes', targetUid!, 'favorites', id));
      // Reset playback if playing track was deleted
      setPlayingIndex(null);
      toast("Canción eliminada de favoritos");
    } catch (err) {
      console.error(err);
      toast("Error al eliminar", "error");
    }
  };
  useEffect(() => {
    if (SUPER_ADMIN_EMAILS.includes(user?.email || '')) {
      const q = collection(db, 'global_song_feedback');
      return onSnapshot(q, (snap) => {
        const feedback: any[] = [];
        snap.forEach(d => feedback.push({ id: d.id, ...d.data() }));
        setGlobalFeedback(feedback.sort((a, b) => b.score - a.score));
      });
    }
  }, [user]);

  const [isZenMode, setIsZenMode] = useState(false);
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const [remoteVolume, setRemoteVolume] = useState(0.4);
  const [centralTextScale, setCentralTextScale] = useState(1);
  const [isFullscreenRequested, setIsFullscreenRequested] = useState(false);
  const [refreshRequestedAt, setRefreshRequestedAt] = useState<number | null>(null);
  const [auraAgentEnabled, setAuraAgentEnabled] = useState(false);
  const [auraAgentWhatsApp, setAuraAgentWhatsApp] = useState('');
  const [newQuote, setNewQuote] = useState<QuoteItem>({ category: '', text: '', price: '', tag: '', ticker: '', imageUrl: '', showClock: false });
  const [newTicker, setNewTicker] = useState('');
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [editingQuoteIndex, setEditingQuoteIndex] = useState<number | null>(null);
  const [editingQuoteType, setEditingQuoteType] = useState<'merchant' | 'aura' | null>(null);
  const [editingTickerIndex, setEditingTickerIndex] = useState<number | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<{ type: 'content' | 'quote' | 'ticker', index: number } | null>(null);
  const [pairingInfo, setPairingInfo] = useState<{ code: string, deviceId: string } | null>(null);
  const [isPairing, setIsPairing] = useState(false);
  const [showToast, setShowToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' | 'info' }>({ show: false, message: '', type: 'info' });
  const [showManualPairing, setShowManualPairing] = useState(false);
  const [showImpulses, setShowImpulses] = useState(false);
  const [tempSchedule, setTempSchedule] = useState<any[]>([]);
  const [manualCircadianSchedule, setManualCircadianSchedule] = useState<any[]>([]);
  const [manualCircadianEnabled, setManualCircadianEnabled] = useState(false);
  const [showCircadianModal, setShowCircadianModal] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [activeTabState, setActiveTabState] = useState<'mando' | 'audio' | 'imagen' | 'ia' | 'config'>('mando');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showImageImpulseModal, setShowImageImpulseModal] = useState<{ show: boolean, imagePath: string | null, duration: number }>({ show: false, imagePath: null, duration: 15 });



  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showAdminInstallHelp, setShowAdminInstallHelp] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const handleInstallAdmin = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);
      setDeferredPrompt(null);
    } else {
      setShowAdminInstallHelp(true);
    }
  };

  const saveMerchantSlot = async (index: number, slotData: QuoteItem) => {
    if (!targetUid) return;
    const newSlots = [...merchantSlots];
    // Ensure array has 5 slots
    while (newSlots.length < 5) newSlots.push({ text: 'Slot Vacío', active: false });
    newSlots[index] = { ...slotData, active: true };

    try {
      await setDoc(doc(db, 'displays', targetUid), { merchantSlots: newSlots }, { merge: true });
      toast("Slot guardado con éxito");
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
    }
  };

  const saveAuraSlot = async (index: number, slotData: QuoteItem) => {
    if (!targetUid || !isAuraStaff) return;
    const newSlots = [...auraSlots];
    while (newSlots.length < 5) newSlots.push({ text: 'Aura Slot Vacío' });
    newSlots[index] = { ...slotData };

    try {
      await setDoc(doc(db, 'displays', targetUid), { auraSlots: newSlots }, { merge: true });
      toast("Slot de Aura guardado");
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
    }
  };

  useEffect(() => {
    // Escuchar el evento de instalación
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log("PWA Install Prompt captured for Admin");
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (searchParams.get('action') === 'install') {
      setShowAdminInstallHelp(true);
      // Limpiar URL para que no vuelva a saltar
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      const searchStr = newParams.toString();
      navigate(`${reactLocation.pathname}${searchStr ? `?${searchStr}` : ''}`, { replace: true });
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [searchParams, navigate, reactLocation.pathname]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        // Fetch current user profile to check role
        const userDoc = await getDoc(doc(db, 'users', u.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubAuth();
  }, [navigate]);

  useEffect(() => {
    if (!targetUid) return;

    // Fetch target user profile to check permissions (like hasAdsPanel)
    const unsubProfile = onSnapshot(doc(db, 'users', targetUid), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setTargetUserProfile(data);
        if (data.slug) setSlug(data.slug);
        if (data.auraCredits !== undefined) setAuraCredits(data.auraCredits);
        else if (data.auraCoins !== undefined) setAuraCredits(data.auraCoins);
      }
    });


    const path = `displays/${targetUid}`;
    const unsub = onSnapshot(doc(db, 'displays', targetUid), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.contents && Array.isArray(data.contents)) {
          setContents(data.contents);
        }
        if (data.quotes && Array.isArray(data.quotes)) {
          setQuotes(data.quotes);
        }
        if (data.merchantSlots && Array.isArray(data.merchantSlots)) {
          setMerchantSlots(data.merchantSlots);
        }
        if (data.auraSlots && Array.isArray(data.auraSlots)) {
          setAuraSlots(data.auraSlots);
        }
        if (data.tickers && Array.isArray(data.tickers)) {
          setTickers(data.tickers);
        }
        if (data.establishmentName) setEstablishmentName(data.establishmentName);
        if (data.adminTitle) setAdminTitle(data.adminTitle);
        if (data.location) setLocation(data.location);
        if (data.theme) setTheme(data.theme);
        if (data.tickerTheme) setTickerTheme(data.tickerTheme);
        if (data.isFullscreenRequested !== undefined) setIsFullscreenRequested(data.isFullscreenRequested);
        if (data.refreshRequestedAt) setRefreshRequestedAt(data.refreshRequestedAt);
        if (data.auraAgentEnabled !== undefined) setAuraAgentEnabled(data.auraAgentEnabled);
        if (data.auraAgentWhatsApp !== undefined) setAuraAgentWhatsApp(data.auraAgentWhatsApp);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, path);
    });

    const unsubClient = onSnapshot(doc(db, 'clientes', targetUid), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.remote_volume !== undefined) setRemoteVolume(data.remote_volume);
        if (data.isZenMode !== undefined) setIsZenMode(data.isZenMode);
        if (data.isControlsVisible !== undefined) setIsControlsVisible(data.isControlsVisible);
        if (data.performanceMode) setPerformanceMode(data.performanceMode);
        if (data.showTicker !== undefined) setShowTicker(data.showTicker);
        if (data.centralTextScale !== undefined) setCentralTextScale(data.centralTextScale);
      }
    });

    return () => {
      unsub();
      unsubClient();
      unsubProfile();
    };
  }, [targetUid]);

  // Handle Pairing Logic
  useEffect(() => {
    const pairCode = searchParams.get('pair');
    if (pairCode && user) {
      const checkPairing = async () => {
        try {
          const docRef = doc(db, 'pairingCodes', pairCode.toUpperCase());
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.expiresAt > Date.now() && !data.linkedClientId) {
              setPairingInfo({ code: pairCode.toUpperCase(), deviceId: data.deviceId });
            } else {
              toast("El código de vinculación ha expirado o ya ha sido usado.", "error");
              // Remove param from URL
              searchParams.delete('pair');
              navigate(`/admin?${searchParams.toString()}`, { replace: true });
            }
          }
        } catch (error) {
          console.error("Error checking pairing code:", error);
        }
      };
      checkPairing();
    }
  }, [searchParams, user, navigate]);

  const handleConfirmPairing = async () => {
    if (!pairingInfo || !user || !targetUid) return;
    setIsPairing(true);
    try {
      const docRef = doc(db, 'pairingCodes', pairingInfo.code);
      await updateDoc(docRef, {
        linkedClientId: targetUid
      });
      alert("¡Pantalla vinculada con éxito!");
      setPairingInfo(null);
      // Remove param from URL
      searchParams.delete('pair');
      navigate(`/admin?${searchParams.toString()}`, { replace: true });
    } catch (error) {
      console.error("Error linking device:", error);
      alert("Error al vincular la pantalla.");
    } finally {
      setIsPairing(false);
    }
  };

  const handleManualPairing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode || !user || !targetUid) return;

    setIsPairing(true);
    try {
      const code = manualCode.toUpperCase().trim();
      const docRef = doc(db, 'pairingCodes', code);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.expiresAt.toDate() > new Date() && !data.linkedClientId) {
          await updateDoc(docRef, {
            linkedClientId: targetUid
          });
          alert("¡Pantalla vinculada con éxito!");
          setShowManualPairing(false);
          setManualCode('');
        } else {
          alert("El código ha expirado o ya ha sido usado.");
        }
      } else {
        alert("Código no válido. Verifica el código en tu TV.");
      }
    } catch (error) {
      console.error("Error in manual pairing:", error);
      alert("Error al vincular. Reintenta.");
    } finally {
      setIsPairing(false);
    }
  };

  const startScanner = async () => {
    setIsScanning(true);
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode("reader");
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            // Handle decoded text
            try {
              const url = new URL(decodedText);
              const pairParam = url.searchParams.get('pair');
              if (pairParam) {
                setManualCode(pairParam.toUpperCase());
                stopScanner();
              }
            } catch (e) {
              // If not a URL, maybe it's just the code
              if (decodedText.length === 6) {
                setManualCode(decodedText.toUpperCase());
                stopScanner();
              }
            }
          },
          () => { } // Error callback (silent)
        );
      } catch (err) {
        console.error("Error starting scanner:", err);
        setIsScanning(false);
        alert("No se pudo acceder a la cámara. Asegúrate de dar permisos.");
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setIsScanning(false);
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        stopScanner();
      }
    };
  }, []);

  const [showConfirmModal, setShowConfirmModal] = useState<{ show: boolean, impulse: any }>({ show: false, impulse: null });
  const [showWarningModal, setShowWarningModal] = useState<{ show: boolean, message: string }>({ show: false, message: '' });
  const [clientConfig, setClientConfig] = useState<any>(null);
  const [showInfoModal, setShowInfoModal] = useState<{ show: boolean, title: string, content: string }>({ show: false, title: '', content: '' });

  const InfoButton = ({ title, content }: { title: string, content: string }) => (
    <button
      onClick={() => setShowInfoModal({ show: true, title, content })}
      className="text-white/20 hover:text-yellow-500 transition-colors"
    >
      <HelpCircle size={14} />
    </button>
  );

  // Toast helper

  const toast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setShowToast({ show: true, message, type });
    setTimeout(() => setShowToast(prev => ({ ...prev, show: false })), 3000);
  };

  useEffect(() => {
    if (showCircadianModal) {
      if (manualCircadianEnabled) {
        setTempSchedule(manualCircadianSchedule);
      } else {
        setTempSchedule(clientConfig?.circadian_schedule || DEFAULT_CIRCADIAN);
      }
    }
  }, [showCircadianModal, clientConfig, manualCircadianEnabled, manualCircadianSchedule, DEFAULT_CIRCADIAN]);

  // Listener for client config (impulses status)
  useEffect(() => {
    if (!targetUid) return;
    const unsub = onSnapshot(doc(db, 'clientes', targetUid), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setClientConfig(data);
        if (data.manual_circadian_schedule !== undefined) setManualCircadianSchedule(data.manual_circadian_schedule);
        if (data.manual_circadian_enabled !== undefined) setManualCircadianEnabled(data.manual_circadian_enabled);
        if (data.auraCredits !== undefined) setAuraCredits(data.auraCredits);
        else if (data.auraCoins !== undefined) setAuraCredits(data.auraCoins);
      }
    });
    return () => unsub();
  }, [targetUid]);

  const logActivity = async (type: 'impulse' | 'upload' | 'session', metadata: any) => {
    if (!targetUid) return;
    try {
      const logRef = doc(collection(db, `displays/${targetUid}/usage_logs`));
      await setDoc(logRef, {
        type,
        metadata,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };

  const updateClientDoc = async (docRef: any, data: any) => {
    try {
      await setDoc(docRef, data, { merge: true });
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, docRef.path);
    }
  };

  const triggerImpulse = async (impulse: any) => {
    if (impulse.id === 'auto') {
      return stopImpulse();
    }

    if (!impulse.hasPlaylist) {
      toast("Próximamente: Esta playlist aún no está disponible.", "info");
      return;
    }

    if (!targetUid) return;

    // Direct trigger now allowed - no need to stop current one first
    setShowConfirmModal({ show: true, impulse });
  };

  const confirmTriggerImpulse = async () => {
    const impulse = showConfirmModal.impulse;
    if (!impulse || !targetUid) return;

    setIsImpulseLoading(impulse.id);
    try {
      await setDoc(doc(db, 'clientes', targetUid), {
        modo_manual: {
          activo: true,
          carpeta: impulse.id,
          id: Math.random().toString(36).substring(7),
          fin: new Date(Date.now() + 3600000) // 1 hour duration
        }
      }, { merge: true });

      // Log the impulse activity
      await logActivity('impulse', {
        impulseId: impulse.id,
        label: impulse.label,
        durationMinutes: 60
      });

      setShowConfirmModal({ show: false, impulse: null });
      toast(`Impulso ${impulse.label} activado`);
    } catch (error) {
      console.error("Error triggering impulse:", error);
      setShowConfirmModal({ show: false, impulse: null });
      toast("Error al activar el impulso", "error");
    } finally {
      setIsImpulseLoading(null);
    }
  };

  const stopImpulse = async () => {
    if (!targetUid) return;
    try {
      await setDoc(doc(db, 'clientes', targetUid), {
        modo_manual: { activo: false }
      }, { merge: true });
      toast('Modo automático restaurado', "success");
    } catch (error) {
      console.error("Error stopping impulse:", error);
      toast("Error al detener el impulso", "error");
    }
  };

  const handleUpdateCircadianSchedule = async (newSchedule: any[]) => {
    if (!targetUid) return;
    console.log("[DEBUG] Saving newSchedule:", newSchedule, manualCircadianEnabled);
    try {
      const updateData: any = {};
      if (manualCircadianEnabled) {
        updateData.manual_circadian_schedule = newSchedule.length > 0 ? newSchedule : null;
        updateData.manual_circadian_enabled = true;
      } else {
        updateData.circadian_schedule = newSchedule; // Still update default
        updateData.manual_circadian_enabled = false;
        // Optionally clear the manual schedule if returning to automatic mode, 
        // to ensure a clean state if switched back
        updateData.manual_circadian_schedule = null;
      }
      await setDoc(doc(db, 'clientes', targetUid), updateData, { merge: true });
      toast("Horario actualizado");
      setManualCircadianSchedule(newSchedule);
      console.log("[DEBUG] Saved successfully");
    } catch (error) {
      console.error("Error updating schedule:", error);
      toast("Error al actualizar horario", "error");
    }
  };

  const isTestClient = user?.email === 'pruebacloud@auradisplay.es' || targetUserProfile?.email === 'pruebacloud@auradisplay.es';
  const canShowImpulses = true;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/login');
    } catch (err) {
      console.error("Error logging out:", err);
    }
  };

  const processImage = (file: File): Promise<Blob> => {
    console.log("DEBUG: Iniciando procesamiento de imagen:", file.name, "Tamaño:", (file.size / 1024 / 1024).toFixed(2), "MB");

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('El procesamiento de la imagen ha tardado demasiado (Timeout 15s)'));
      }, 15000);

      const img = new Image();
      img.onload = () => {
        clearTimeout(timeout);
        console.log("DEBUG: Imagen cargada. Dimensiones originales:", img.width, "x", img.height);

        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('No se pudo obtener el contexto del canvas');
          }

          const targetWidth = 1920;
          const targetHeight = 1080;
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          ctx.fillStyle = 'black';
          ctx.fillRect(0, 0, targetWidth, targetHeight);

          const scale = Math.min(targetWidth / img.width, targetHeight / img.height);
          const x = (targetWidth / 2) - (img.width / 2) * scale;
          const y = (targetHeight / 2) - (img.height / 2) * scale;

          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          console.log("DEBUG: Imagen dibujada en canvas 16:9");

          canvas.toBlob((blob) => {
            if (blob) {
              console.log("DEBUG: Blob generado con éxito. Tamaño final:", (blob.size / 1024).toFixed(2), "KB");
              resolve(blob);
            } else {
              reject(new Error('Error al generar el blob (resultado nulo)'));
            }
          }, 'image/jpeg', 0.8);
        } catch (e: any) {
          console.error("DEBUG: Error interno en canvas:", e);
          reject(e);
        } finally {
          URL.revokeObjectURL(img.src);
        }
      };

      img.onerror = (e) => {
        clearTimeout(timeout);
        console.error("DEBUG: Error al cargar objeto Image:", e);
        URL.revokeObjectURL(img.src);
        reject(new Error('Error al cargar la imagen. Asegúrate de que es un archivo de imagen válido.'));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const performUpload = async (file: File) => {
    if (!file || !user || !targetUid) return;

    if (contents.length >= 20) {
      alert("Límite de 20 imágenes alcanzado.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("La imagen supera el límite de 2MB recomendado para un rendimiento óptimo y ahorro de espacio.");
      return;
    }

    setUploading(true);
    try {
      console.log("DEBUG: Iniciando upload para:", file.name);
      const processedBlob = await processImage(file);

      const fileName = `${Date.now()}_${file.name.replace(/\.[^/.]+$/, "")}.jpg`;
      const storagePath = `contenidos/${targetUid}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      console.log("DEBUG: Subiendo a Storage:", storagePath);
      await uploadBytes(storageRef, processedBlob, { contentType: 'image/jpeg' });

      console.log("DEBUG: Obteniendo URL de descarga...");
      const url = await getDownloadURL(storageRef);

      const newItem: ContentItem = {
        url,
        name: fileName,
        createdAt: Date.now(),
        storagePath
      };

      // Guardar en Firestore
      console.log("DEBUG: Guardando en Firestore...");
      const displayRef = doc(db, 'displays', targetUid);

      await setDoc(displayRef, {
        contents: arrayUnion(newItem)
      }, { merge: true });

      console.log("DEBUG: ¡Éxito total!");

      // Log the upload activity
      await logActivity('upload', {
        fileName,
        size: processedBlob.size
      });

      // Auto-populate slide form if image URL is empty
      if (!newQuote.imageUrl) {
        setNewQuote(prev => ({ ...prev, imageUrl: url }));
      }
    } catch (err: any) {
      console.error("DEBUG: Error en upload:", err);
      let errorMsg = "Error al subir la imagen.";

      if (err.message?.includes('Timeout')) errorMsg = "El procesamiento tardó demasiado. Prueba con una imagen más pequeña.";
      if (err.code === 'storage/unauthorized') errorMsg = "No tienes permisos para subir archivos (Error de Storage).";
      if (err.code === 'storage/retry-limit-exceeded') errorMsg = "Error de red al subir. Reintenta.";

      alert(`${errorMsg}\n\nDetalle: ${err.message || err.code || 'Error desconocido'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) performUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await performUpload(file);
    }
  };

  const handleDelete = async (item: ContentItem) => {
    if (!targetUid || !confirm(`¿Estás seguro de eliminar "${item.name}"?`)) return;

    try {
      // Delete from Storage (safe wrapper)
      try {
        const storageRef = ref(storage, item.storagePath);
        await deleteObject(storageRef);
      } catch (storageErr) {
        console.warn("Error deleting from storage (non-blocking):", storageErr);
      }

      // Delete from Firestore
      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, {
        contents: arrayRemove(item)
      }, { merge: true });
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
      alert("Error al eliminar el contenido.");
    }
  };

  const handleCreateImageImpulse = async () => {
    if (!targetUid || !showImageImpulseModal.imagePath) return;

    try {
      let updatedMerchantSlots = [...merchantSlots];
      while (updatedMerchantSlots.length < 5) updatedMerchantSlots.push({ text: 'Disponible', active: false });

      let targetIndex = updatedMerchantSlots.findIndex(s => !s.active || s.text === 'Disponible');
      if (targetIndex === -1) {
        targetIndex = 0; // Replace slot 1 if all full
      }

      const backup = { ...updatedMerchantSlots[targetIndex] };
      const expiry = new Date();
      if (showImageImpulseModal.duration > 0) {
        expiry.setMinutes(expiry.getMinutes() + showImageImpulseModal.duration);
      } else {
        // "Permanent" mode -> 100 years into the future
        expiry.setFullYear(expiry.getFullYear() + 100);
      }

      updatedMerchantSlots[targetIndex] = {
        text: `Impulso Visual`,
        category: `Duración: ${showImageImpulseModal.duration > 0 ? showImageImpulseModal.duration + 'm' : 'ILIMITADA'}`,
        imageUrl: showImageImpulseModal.imagePath,
        active: true,
        isImpulse: true,
        expiresAt: expiry.toISOString(),
        backupSlot: backup.active ? backup : null
      };

      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, {
        merchantSlots: updatedMerchantSlots
      }, { merge: true });

      toast("¡Impulso visual activado! Reemplazado en el Slot " + (targetIndex + 1), "success");
      setShowImageImpulseModal({ show: false, imagePath: null, duration: 15 });
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
      toast("Error al configurar el impulso.", "error");
    }
  };

  const handleAddQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasContent = newQuote.text || newQuote.imageUrl || newQuote.category || newQuote.price || newQuote.tag;
    if (!targetUid || !hasContent) return;

    console.log("DEBUG: handleAddQuote - newQuote:", newQuote);
    console.log("DEBUG: handleAddQuote - editingQuoteIndex:", editingQuoteIndex);

    try {
      const displayRef = doc(db, 'displays', targetUid);
      if (editingQuoteIndex !== null) {
        const updatedQuotes = [...quotes];
        updatedQuotes[editingQuoteIndex] = newQuote;
        console.log("DEBUG: handleAddQuote - Updating Firestore with:", updatedQuotes);
        await setDoc(displayRef, { quotes: updatedQuotes }, { merge: true });
        setEditingQuoteIndex(null);
        alert("Slide actualizado.");
      } else {
        await setDoc(displayRef, {
          quotes: arrayUnion(newQuote)
        }, { merge: true });
        alert("¡Slide añadido con éxito!");
      }
      setNewQuote({ category: '', text: '', price: '', tag: '', ticker: '', imageUrl: '', showClock: false });
    } catch (err) {
      console.error("DEBUG: handleAddQuote - Error:", err);
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
    }
  };

  const handleAddTicker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetUid || !newTicker) return;

    try {
      const displayRef = doc(db, 'displays', targetUid);
      if (editingTickerIndex !== null) {
        const updatedTickers = [...tickers];
        updatedTickers[editingTickerIndex] = { ...updatedTickers[editingTickerIndex], text: newTicker };
        await setDoc(displayRef, { tickers: updatedTickers }, { merge: true });
        setEditingTickerIndex(null);
      } else {
        await setDoc(displayRef, {
          tickers: arrayUnion({ text: newTicker })
        }, { merge: true });
      }
      setNewTicker('');
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
    }
  };

  const startEditingQuote = (index: number) => {
    setNewQuote(quotes[index]);
    setEditingQuoteIndex(index);
    document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const startEditingTicker = (index: number) => {
    setNewTicker(tickers[index].text);
    setEditingTickerIndex(index);
    document.getElementById('ticker-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const downloadSalesKit = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Background - Dark Anthracite
    doc.setFillColor(20, 20, 20);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Header Glow / Accent
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.5);
    doc.line(10, 10, pageWidth - 10, 10);
    doc.line(10, pageHeight - 10, pageWidth - 10, pageHeight - 10);

    // Title
    doc.setFontSize(28);
    doc.setTextColor(212, 175, 55); // Gold
    doc.setFont("helvetica", "bold");
    doc.text("AURA BUSINESS", pageWidth / 2, 30, { align: 'center' });

    doc.setFontSize(14);
    doc.setTextColor(150, 150, 150);
    doc.setFont("helvetica", "normal");
    doc.text("Dossier de Rentabilidad y Ecosistema Digital", pageWidth / 2, 40, { align: 'center' });

    let y = 60;

    // Pillar I: El Cerebro (Visual)
    doc.setFontSize(16);
    doc.setTextColor(212, 175, 55);
    doc.setFont("helvetica", "bold");
    doc.text("I. EL CEREBRO: Gestión de Contenidos (Visual)", 20, y);
    y += 12;
    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    const visualPoints = [
      { t: "Smart Signage:", d: "Convierte cualquier TV en un canal corporativo de alta definición." },
      { t: "Venta Cruzada Dinámica:", d: "Usa el Ticker para promocionar productos mientras suena la música." },
      { t: "Actualización Instantánea:", d: "Cambia precios o mensajes desde tu móvil y se reflejan al segundo." }
    ];
    visualPoints.forEach(p => {
      doc.setFont("helvetica", "bold");
      doc.text(p.t, 25, y);
      const titleWidth = doc.getTextWidth(p.t);
      doc.setFont("helvetica", "normal");
      const descLines = doc.splitTextToSize(p.d, pageWidth - 40 - titleWidth - 5);
      doc.text(descLines, 25 + titleWidth + 3, y);
      y += Math.max(8, descLines.length * 5 + 3);
    });

    y += 5;

    // Pillar II: El Alma (Audio)
    doc.setFontSize(16);
    doc.setTextColor(212, 175, 55);
    doc.setFont("helvetica", "bold");
    doc.text("II. EL ALMA: Inteligencia Acústica (Audio)", 20, y);
    y += 12;

    // SGAE Highlight Box
    doc.setDrawColor(212, 175, 55);
    doc.setFillColor(30, 30, 30);
    doc.rect(20, y, pageWidth - 40, 15, 'FD');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text("EXENCIÓN LEGAL SGAE/AGEDI (Art. 157 LPI)", pageWidth / 2, y + 10, { align: 'center' });
    y += 25;

    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    const audioPoints = [
      { t: "Curación por IA:", d: "Música que se adapta al flujo de clientes (BPM variable según horario)." },
      { t: "Audio Branding:", d: "Identidad sonora profesional diseñada para tu tipo de negocio." }
    ];
    audioPoints.forEach(p => {
      doc.setFont("helvetica", "bold");
      doc.text(p.t, 25, y);
      const titleWidth = doc.getTextWidth(p.t);
      doc.setFont("helvetica", "normal");
      const descLines = doc.splitTextToSize(p.d, pageWidth - 40 - titleWidth - 5);
      doc.text(descLines, 25 + titleWidth + 3, y);
      y += Math.max(8, descLines.length * 5 + 3);
    });

    y += 5;

    // Pillar III: El Control (Hardware)
    doc.setFontSize(16);
    doc.setTextColor(212, 175, 55);
    doc.setFont("helvetica", "bold");
    doc.text("III. EL CONTROL: Despliegue Cloud (Hardware)", 20, y);
    y += 12;
    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    const hardwarePoints = [
      { t: "Cero Inversión:", d: "Sin reproductores costosos. Tu Smart TV es el hardware." },
      { t: "Gestión Multi-Sede:", d: "Controla todos tus locales desde un único panel centralizado." }
    ];
    hardwarePoints.forEach(p => {
      doc.setFont("helvetica", "bold");
      doc.text(p.t, 25, y);
      const titleWidth = doc.getTextWidth(p.t);
      doc.setFont("helvetica", "normal");
      const descLines = doc.splitTextToSize(p.d, pageWidth - 40 - titleWidth - 5);
      doc.text(descLines, 25 + titleWidth + 3, y);
      y += Math.max(8, descLines.length * 5 + 3);
    });

    y += 10;

    // Sector Application Table
    doc.setFontSize(14);
    doc.setTextColor(212, 175, 55);
    doc.text("Aplicación por Sector", 20, y);
    y += 8;

    // Table Header
    doc.setFillColor(40, 40, 40);
    doc.rect(20, y, pageWidth - 40, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text("Sector", 25, y + 7);
    doc.text("Uso del Ticker (Info Dinámica)", 55, y + 7);
    doc.text("Estilo Musical IA", 145, y + 7);
    y += 10;

    const sectors = [
      { s: "Gimnasios", t: "Próxima clase de Zumba en 10 min", m: "High Energy / Tech-House" },
      { s: "Hoteles", t: "Check-out hasta las 12:00h - Feliz estancia", m: "Deep House / Lounge" },
      { s: "Retail", t: "2x1 en zona de probadores solo hoy", m: "Pop Curado / Trendy" },
      { s: "Clínicas", t: "Turno para el paciente 45 en sala 2", m: "Zen Ambient / Relax" }
    ];

    sectors.forEach(s => {
      doc.setDrawColor(50, 50, 50);
      doc.line(20, y + 12, pageWidth - 20, y + 12);
      doc.setTextColor(180, 180, 180);
      doc.setFont("helvetica", "bold");
      doc.text(s.s, 25, y + 7);
      doc.setFont("helvetica", "normal");
      const tickerLines = doc.splitTextToSize(s.t, 85);
      doc.text(tickerLines, 55, y + 7);
      doc.text(s.m, 145, y + 7);
      y += Math.max(12, tickerLines.length * 5 + 2);
    });

    // Elevator Pitch
    y = 260;
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(1);
    doc.line(20, y, pageWidth - 20, y);
    y += 10;
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "italic");
    const pitch = "Aura Business: La única plataforma que paga su propia suscripción eliminando multas legales y aumentando tu ticket medio mediante señalética inteligente.";
    doc.text(pitch, pageWidth / 2, y, { align: 'center', maxWidth: 160 });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    doc.text("© 2026 Aura Business - Dossier de Rentabilidad y Prestaciones", pageWidth / 2, 285, { align: 'center' });

    doc.save("Aura_Business_Dossier_Rentabilidad.pdf");
  };

  const handleDeleteAllContents = async () => {
    if (!targetUid || !confirm("¿Estás seguro de eliminar TODAS las imágenes? Esta acción no se puede deshacer.")) return;

    setUploading(true);
    try {
      // Delete all from Storage
      for (const item of contents) {
        try {
          const storageRef = ref(storage, item.storagePath);
          await deleteObject(storageRef);
        } catch (e) {
          console.error("Error deleting from storage:", item.storagePath, e);
        }
      }

      // Clear Firestore
      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, {
        contents: []
      }, { merge: true });
      console.log("DEBUG: Todas las imágenes eliminadas.");
    } catch (err) {
      console.error("Error deleting all contents:", err);
      alert("Error al eliminar todas las imágenes.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAllQuotes = async () => {
    if (!targetUid || !confirm("¿Estás seguro de eliminar TODOS los textos?")) return;

    try {
      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, {
        quotes: []
      }, { merge: true });
    } catch (err) {
      console.error("Error deleting all quotes:", err);
      alert("Error al eliminar todos los textos.");
    }
  };

  const handleResetToDefaultQuotes = async () => {
    if (!targetUid || !confirm("¿Restablecer slides al diseño por defecto?")) return;
    try {
      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, { quotes: DEFAULT_QUOTES }, { merge: true });
      toast("Slides restablecidos");
    } catch (err) {
      console.error("Error resetting quotes:", err);
      alert("Error al restablecer los slides.");
    }
  };

  const handleDeleteQuote = async (quote: QuoteItem) => {
    if (!targetUid) return;
    try {
      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, {
        quotes: arrayRemove(quote)
      }, { merge: true });
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
    }
  };

  const handleDeleteTicker = async (ticker: TickerItem) => {
    if (!targetUid) return;
    try {
      const displayRef = doc(db, 'displays', targetUid);
      await setDoc(displayRef, {
        tickers: arrayRemove(ticker)
      }, { merge: true });
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `displays/${targetUid}`);
    }
  };

  const handleUpdateSchedule = async (schedule: Schedule) => {
    if (!targetUid || !editingSchedule) return;

    try {
      const displayRef = doc(db, 'displays', targetUid);
      let newItems;
      let field;

      if (editingSchedule.type === 'content') {
        newItems = [...contents];
        field = 'contents';
      } else if (editingSchedule.type === 'quote') {
        newItems = [...quotes];
        field = 'quotes';
      } else {
        newItems = [...tickers];
        field = 'tickers';
      }

      // @ts-ignore
      newItems[editingSchedule.index].schedule = schedule;

      await setDoc(displayRef, {
        [field]: newItems
      }, { merge: true });

      setEditingSchedule(null);
    } catch (err) {
      console.error("Error updating schedule:", err);
      alert("Error al guardar el horario.");
    }
  };

  const ScheduleModal = ({ item, onSave, onClose }: { item: ContentItem | QuoteItem | TickerItem, onSave: (s: Schedule) => void, onClose: () => void }) => {
    const [schedule, setSchedule] = useState<Schedule>(item.schedule || {
      enabled: false,
      startTime: '00:00',
      endTime: '23:59',
      days: [0, 1, 2, 3, 4, 5, 6]
    });

    const toggleDay = (day: number) => {
      setSchedule(prev => ({
        ...prev,
        days: prev.days.includes(day)
          ? prev.days.filter(d => d !== day)
          : [...prev.days, day].sort()
      }));
    };

    const daysOfWeek = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="w-full max-w-md rounded-3xl border border-white/10 bg-[#111] p-8 shadow-2xl"
        >
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="text-white/60" size={20} />
              <h3 className="text-xl font-semibold">Configurar Horario</h3>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/5 p-2 hover:bg-white/10">
              <X size={20} />
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-2xl bg-white/5 p-4">
              <span className="text-sm font-medium">Activar Horario</span>
              <button
                onClick={() => setSchedule(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`h-6 w-12 rounded-full transition-colors ${schedule.enabled ? 'bg-green-500' : 'bg-white/10'}`}
              >
                <div className={`h-4 w-4 rounded-full bg-white transition-transform ${schedule.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className={`space-y-4 transition-opacity ${schedule.enabled ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
              {!schedule.enabled && (
                <p className="text-[10px] text-white/40 italic text-center">
                  Si el horario está desactivado, el contenido se mostrará siempre.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Desde</label>
                  <input
                    type="time"
                    value={schedule.startTime}
                    onChange={(e) => setSchedule(prev => ({ ...prev, startTime: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Hasta</label>
                  <input
                    type="time"
                    value={schedule.endTime}
                    onChange={(e) => setSchedule(prev => ({ ...prev, endTime: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Días de la semana</label>
                <div className="flex justify-between gap-2">
                  {daysOfWeek.map((day, idx) => (
                    <button
                      key={idx}
                      onClick={() => toggleDay(idx)}
                      className={`h-10 w-10 rounded-xl text-xs font-bold transition-all ${schedule.days.includes(idx) ? 'bg-white text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'
                        }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => onSave(schedule)}
              className="w-full rounded-2xl bg-white py-4 text-sm font-bold text-black transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Guardar Configuración
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const displayUrl = `https://www.auradisplay.es/${slug || 'negocio'}`;


  const qrRef = useRef<HTMLDivElement>(null);

  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'auradisplay-qr.png';
      link.href = url;
      link.click();
    }
  };

  const handleShareQR = async () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      canvas.toBlob(async (blob) => {
        if (blob && navigator.share) {
          const file = new File([blob], 'auradisplay-qr.png', { type: 'image/png' });
          await navigator.share({
            files: [file],
            title: 'Aura Digital Pass',
            text: `Accede a mi display Aura: ${displayUrl}`
          }).catch(console.error);
        } else {
          alert("Tu navegador no soporta compartir imágenes directamente.");
        }
      });
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(displayUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUpdateConfig = async () => {
    if (!user || !targetUid) return;
    try {
      const displayRef = doc(db, 'displays', targetUid);
      const userRef = doc(db, 'users', targetUid);

      const cleanSlug = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');

      await Promise.all([
        setDoc(displayRef, {
          establishmentName,
          adminTitle,
          location,
          theme,
          tickerTheme,
          isFullscreenRequested,
          refreshRequestedAt,
          auraAgentEnabled,
          auraAgentWhatsApp,
          updatedAt: Date.now()
        }, { merge: true }),
        setDoc(doc(db, "clientes", targetUid), {
          remote_volume: remoteVolume,
          isZenMode,
          isControlsVisible,
          performanceMode,
          showTicker
        }, { merge: true }),
        updateDoc(userRef, {
          slug: cleanSlug
        })
      ]);
      setSlug(cleanSlug);
      toast("Configuración actualizada", "success");
    } catch (err) {
      console.error("Error updating config:", err);
      toast("Error al actualizar configuración", "error");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-white/10">
      {/* Pairing Overlay */}
      <AnimatePresence>
        {pairingInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="w-full max-w-md rounded-[2.5rem] border border-white/10 bg-[#111] p-10 text-center shadow-2xl"
            >
              <div className="mb-6 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
                  <Tv className="text-white" size={32} />
                </div>
              </div>

              <h2 className="mb-2 text-2xl font-bold tracking-tight">Vincular Nueva Pantalla</h2>
              <p className="mb-8 text-sm text-white/50">
                ¿Deseas vincular esta Smart TV a tu cuenta de <b>{establishmentName || userProfile?.email}</b>?
              </p>

              <div className="mb-10 rounded-2xl bg-white/5 p-6 border border-white/5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/30 block mb-2">Código de Dispositivo</span>
                <span className="text-3xl font-mono font-black tracking-widest text-white">{pairingInfo.code}</span>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleConfirmPairing}
                  disabled={isPairing}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-4 text-sm font-bold text-black transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                >
                  {isPairing ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                  Confirmar Vinculación
                </button>
                <button
                  onClick={() => {
                    setPairingInfo(null);
                    searchParams.delete('pair');
                    navigate(`/admin?${searchParams.toString()}`, { replace: true });
                  }}
                  disabled={isPairing}
                  className="w-full rounded-2xl bg-white/5 py-4 text-sm font-bold text-white/60 transition-all hover:bg-white/10"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual Pairing Modal */}
      <AnimatePresence>
        {showManualPairing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl p-6"
            onClick={() => setShowManualPairing(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="w-full max-w-md rounded-[2.5rem] border border-white/10 bg-[#111] p-10 text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-6 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
                  <Tv className="text-white" size={32} />
                </div>
              </div>

              <h2 className="mb-2 text-2xl font-bold tracking-tight">Vincular Smart TV</h2>
              <p className="mb-8 text-sm text-white/50">
                Escanea el código QR de tu TV o introduce el código de 6 dígitos manualmente.
              </p>

              <div className="space-y-6">
                {isScanning ? (
                  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black">
                    <div id="reader" className="w-full aspect-square"></div>
                    <button
                      onClick={stopScanner}
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-red-500 px-6 py-2 text-[10px] font-bold uppercase tracking-widest text-white shadow-xl tv-focus"
                    >
                      Detener Cámara
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={startScanner}
                    className="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-6 transition-all hover:bg-white/10 group tv-focus"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 group-hover:bg-white group-hover:text-black transition-all">
                      <Scan size={24} />
                    </div>
                    <div className="text-left">
                      <span className="block text-sm font-bold text-white">Escanear Código QR</span>
                      <span className="block text-[10px] text-white/40 uppercase tracking-widest">Usar cámara del móvil</span>
                    </div>
                  </button>
                )}

                <div className="relative flex items-center py-4">
                  <div className="flex-grow border-t border-white/5"></div>
                  <span className="mx-4 flex-shrink text-[10px] font-bold uppercase tracking-widest text-white/20">O introduce el código</span>
                  <div className="flex-grow border-t border-white/5"></div>
                </div>

                <form onSubmit={handleManualPairing} className="space-y-6">
                  <div className="relative">
                    <input
                      type="text"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                      placeholder="ABCDEF"
                      maxLength={6}
                      className="w-full rounded-2xl border border-white/10 bg-white/5 px-6 py-5 text-center text-4xl font-mono font-black tracking-[0.3em] text-white placeholder:text-white/10 focus:border-white/20 focus:outline-none tv-focus"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      type="submit"
                      disabled={isPairing || manualCode.length < 6}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-4 text-sm font-bold text-black transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 tv-focus"
                    >
                      {isPairing ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                      Vincular Dispositivo
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        stopScanner();
                        setShowManualPairing(false);
                      }}
                      className="w-full rounded-2xl bg-white/5 py-4 text-sm font-bold text-white/60 transition-all hover:bg-white/10 tv-focus"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Warning Modal (Stop first) */}
      <AnimatePresence>
        {showWarningModal.show && (
          <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-zinc-900 shadow-2xl"
            >
              <div className="p-8 text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-500/10 text-red-500">
                  <AlertTriangle size={40} />
                </div>
                <h3 className="mb-2 text-2xl font-bold text-white">Impulso en Curso</h3>
                <p className="text-zinc-400">
                  {showWarningModal.message}
                </p>
              </div>
              <div className="p-4 border-t border-white/5">
                <button
                  onClick={() => setShowWarningModal({ show: false, message: '' })}
                  className="w-full rounded-2xl bg-white/10 py-4 text-sm font-bold text-white transition-colors hover:bg-white/20"
                >
                  ENTENDIDO
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[250] px-6 py-3 rounded-full font-bold text-xs uppercase tracking-widest shadow-2xl flex items-center gap-3 ${showToast.type === 'error' ? 'bg-red-500 text-white' :
                showToast.type === 'info' ? 'bg-blue-500 text-white' :
                  'bg-yellow-500 text-black'
              }`}
          >
            {showToast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
            {showToast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmModal.show && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-zinc-900 shadow-2xl"
            >
              <div className="p-8 text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-yellow-500/10 text-yellow-500">
                  <Activity size={40} />
                </div>
                <h3 className="mb-2 text-2xl font-bold text-white">Confirmar Impulso</h3>
                <p className="text-zinc-400">
                  ¿Vas a activar el modo <span className="font-bold text-white">{showConfirmModal.impulse?.label}</span>?
                </p>
                <p className="mt-2 text-sm text-zinc-500">
                  Esto detendrá la música actual para reproducir la playlist seleccionada.
                </p>
              </div>
              <div className="flex border-t border-white/5">
                <button
                  onClick={() => setShowConfirmModal({ show: false, impulse: null })}
                  className="flex-1 px-6 py-4 text-sm font-bold text-zinc-400 transition-colors hover:bg-white/5"
                >
                  CANCELAR
                </button>
                <button
                  onClick={confirmTriggerImpulse}
                  className="flex-1 bg-yellow-500 px-6 py-4 text-sm font-bold text-black transition-colors hover:bg-yellow-400"
                >
                  ACTIVAR AHORA
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Impulses Modal */}
      <AnimatePresence>
        {showImpulses && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[140] flex items-center justify-center bg-black/90 p-6 backdrop-blur-xl"
            onClick={() => setShowImpulses(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-2xl rounded-[3rem] border border-white/10 bg-black/60 p-10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="font-serif text-3xl italic tracking-tight text-white">Impulsos Aura</h3>
                  <p className="text-xs text-white/40 uppercase tracking-widest mt-1">Panel de Control Comercial</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {COMMERCIAL_IMPULSES.map((impulse) => {
                  console.log("DEBUG: rendering impulse", impulse.id);
                  const isStandardAuto = !clientConfig?.modo_manual?.activo && !clientConfig?.manual_circadian_enabled;
                  const isActive = (impulse.id === 'auto' && (isStandardAuto || (clientConfig?.manual_circadian_enabled && impulse.id === 'auto'))) ||
                    (clientConfig?.modo_manual?.activo && clientConfig?.modo_manual?.carpeta === impulse.id);
                  const isOtherActive = (clientConfig?.modo_manual?.activo && clientConfig?.modo_manual?.carpeta !== impulse.id) ||
                    (clientConfig?.manual_circadian_enabled && impulse.id !== 'auto' && !clientConfig?.modo_manual?.activo);

                  return (
                    <button
                      key={impulse.id}
                      onClick={() => triggerImpulse(impulse)}
                      className={`flex items-start gap-4 p-4 rounded-2xl border transition-all text-left group tv-focus cursor-pointer ${isActive
                          ? 'border-yellow-500 bg-yellow-500/10 !cursor-default'
                          : 'border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/10'
                        }`}
                    >
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl group-hover:scale-110 transition-transform ${isActive ? 'bg-yellow-500 text-black' : 'bg-white/5'
                        }`}>
                        {isImpulseLoading === impulse.id ? (
                          <Loader2 className="animate-spin" size={24} />
                        ) : (
                          impulse.icon
                        )}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-bold transition-colors ${isActive ? 'text-yellow-500' : 'text-white group-hover:text-yellow-400'
                            }`}>
                            {impulse.label}
                            {isActive && impulse.id === 'auto' && clientConfig?.manual_circadian_enabled && " (ACTIVO - HORARIO MANUAL)"}
                            {isActive && !(impulse.id === 'auto' && clientConfig?.manual_circadian_enabled) && " (ACTIVO)"}
                          </p>
                        </div>
                        <p className="text-[10px] text-white/40 leading-relaxed font-serif italic">{impulse.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setShowImpulses(false)}
                className="mt-10 w-full rounded-full bg-white/10 py-4 text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-all hover:bg-white hover:text-black tv-focus"
              >
                Cerrar Panel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Impulse Modal */}
      <AnimatePresence>
        {showImageImpulseModal.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center bg-black/90 p-6 backdrop-blur-xl"
            onClick={() => setShowImageImpulseModal({ show: false, imagePath: null, duration: 15 })}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-lg rounded-[3rem] border border-blue-500/20 bg-zinc-900 p-8 shadow-2xl relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-blue-400" />
              <div className="mb-6">
                <h3 className="font-serif text-3xl italic tracking-tight text-white flex items-center gap-2">
                  <Zap size={28} className="text-blue-500" />
                  Lanzar Impulso Visual
                </h3>
                <p className="text-xs text-white/40 mt-2 leading-relaxed">
                  Esta imagen reemplazará temporalmente tu primer Slot de Impulso disponible durante el tiempo que selecciones. Pasado el tiempo, el contenido anterior se restablecerá automáticamente.
                </p>
              </div>

              {showImageImpulseModal.imagePath && (
                <div className="w-full aspect-video rounded-2xl overflow-hidden bg-black/50 border border-white/10 mb-8 relative">
                  <img src={showImageImpulseModal.imagePath} className="w-full h-full object-cover opacity-80" alt="Preview" referrerPolicy="no-referrer" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
              )}

              <div className="space-y-3 mb-8">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-2">Selecciona la duración (minutos):</p>
                <div className="grid grid-cols-4 gap-2">
                  {[15, 30, 60, 0].map(val => (
                    <button
                      key={val}
                      onClick={() => setShowImageImpulseModal({ ...showImageImpulseModal, duration: val })}
                      className={`py-3 rounded-xl text-xs font-bold transition-all ${showImageImpulseModal.duration === val
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                          : 'bg-white/5 text-white/40 hover:bg-white/10'
                        }`}
                    >
                      {val === 0 ? 'ILIMITADO' : `${val} MIN`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowImageImpulseModal({ show: false, imagePath: null, duration: 15 })}
                  className="flex-1 rounded-2xl bg-white/5 py-4 text-[10px] font-bold uppercase tracking-widest text-white transition-all hover:bg-white/10"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateImageImpulse}
                  className="flex-[2] rounded-2xl bg-blue-500 py-4 text-[12px] font-black uppercase tracking-widest text-white transition-all hover:bg-blue-400 shadow-xl shadow-blue-500/20"
                >
                  Lanzar Ahora
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Circadian Schedule Modal */}
      <AnimatePresence>
        {showInfoModal.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md"
            onClick={() => setShowInfoModal({ show: false, title: '', content: '' })}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-black border border-white/10 p-8 rounded-3xl max-w-sm w-full"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-2">{showInfoModal.title}</h3>
              <p className="text-sm text-white/60 mb-6">{showInfoModal.content}</p>
              <button
                onClick={() => setShowInfoModal({ show: false, title: '', content: '' })}
                className="w-full bg-white text-black py-3 rounded-xl font-bold uppercase text-xs"
              >
                Entendido
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showCircadianModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center bg-black/95 p-6 backdrop-blur-2xl"
            onClick={() => setShowCircadianModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="w-full max-w-3xl rounded-[2rem] sm:rounded-[3rem] border border-white/10 bg-zinc-900 p-6 sm:p-10 shadow-2xl relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowCircadianModal(false)}
                className="absolute top-6 right-6 p-2 rounded-full bg-white/5 text-white/40 hover:bg-white/10 hover:text-white transition-all sm:hidden"
              >
                <X size={20} />
              </button>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-2xl sm:text-3xl italic tracking-tight text-white">Configurar Horario</h3>
                    <span className="px-2 py-0.5 rounded-md bg-blue-500/20 border border-blue-500/30 text-[8px] font-black tracking-widest text-blue-400 uppercase">Beta / Dev</span>
                  </div>
                  <button
                    onClick={() => {
                      const newState = !manualCircadianEnabled;
                      setManualCircadianEnabled(newState);
                      if (newState && (manualCircadianSchedule || []).length === 0) setTempSchedule(DEFAULT_CIRCADIAN);
                      else if (newState) setTempSchedule(manualCircadianSchedule);
                      else setTempSchedule(clientConfig?.circadian_schedule || DEFAULT_CIRCADIAN);
                    }}
                    className={`flex items-center gap-2 px-3 py-1 rounded-full w-fit text-[10px] font-black uppercase tracking-widest transition-all ${manualCircadianEnabled ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-white/5 text-white/40 border border-white/5'}`}
                  >
                    <Activity size={10} />
                    {manualCircadianEnabled ? 'Modo Manual (Impulso) Activo' : 'Modo Automático (Por defecto)'}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (window.confirm("¿Restablecer al horario estándar de Aura?")) {
                        setTempSchedule(DEFAULT_CIRCADIAN);
                      }
                    }}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-white/60 transition-all hover:bg-white hover:text-black"
                  >
                    Restablecer
                  </button>
                  {manualCircadianEnabled && (
                    <button
                      onClick={() => {
                        if (window.confirm("¿Estás seguro de que quieres borrar todo el horario manual y empezar de cero?")) {
                          setTempSchedule([]);
                        }
                      }}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 rounded-full border border-red-500/20 bg-red-500/10 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-red-400 transition-all hover:bg-red-500 hover:text-white"
                    >
                      Borrar Todo
                    </button>
                  )}
                </div>
              </div>

              <div className="grid gap-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                {(tempSchedule || []).map((slot: any, idx: number) => (
                  <div key={idx} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 rounded-2xl bg-white/5 p-4 border border-white/5">
                    <div className="grid grid-cols-2 sm:flex sm:items-center gap-4 flex-1">
                      <div className="space-y-1">
                        <label className="text-[8px] font-black uppercase tracking-widest text-white/30 block ml-1">Inicio (H)</label>
                        <select
                          value={slot.start}
                          onChange={(e) => {
                            console.log("[DEBUG] Changing start:", e.target.value);
                            const newSched = [...tempSchedule];
                            newSched[idx] = { ...slot, start: parseInt(e.target.value) };
                            setTempSchedule(newSched);
                          }}
                          className="w-full rounded-xl bg-zinc-800 border border-white/10 p-3 text-sm font-mono text-white focus:outline-none focus:border-yellow-500/50 appearance-none cursor-pointer hover:bg-zinc-700 transition-all !text-white !bg-zinc-800"
                        >
                          {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{h < 10 ? `0${h}` : h}:00</option>)}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[8px] font-black uppercase tracking-widest text-white/30 block ml-1">Fin (H)</label>
                        <select
                          value={slot.end}
                          onChange={(e) => {
                            console.log("[DEBUG] Changing end:", e.target.value);
                            const newSched = [...tempSchedule];
                            newSched[idx] = { ...slot, end: parseInt(e.target.value) };
                            setTempSchedule(newSched);
                          }}
                          className="w-full rounded-xl bg-zinc-800 border border-white/10 p-3 text-sm font-mono text-white focus:outline-none focus:border-yellow-500/50 appearance-none cursor-pointer hover:bg-zinc-700 transition-all !text-white !bg-zinc-800"
                        >
                          {Array.from({ length: 25 }, (_, i) => i).filter(h => h > 0).map((h) => <option key={h} value={h}>{h < 10 ? `0${h}` : h}:00</option>)}
                        </select>
                      </div>
                      <div className="col-span-2 sm:flex-[2] space-y-1">
                        <label className="text-[8px] font-black uppercase tracking-widest text-white/30 block ml-1">Carpeta de Playlist</label>
                        <select
                          value={slot.folder}
                          onChange={(e) => {
                            console.log("[DEBUG] Changing folder:", e.target.value);
                            const newSched = [...tempSchedule];
                            newSched[idx] = { ...slot, folder: e.target.value };
                            setTempSchedule(newSched);
                          }}
                          className="w-full rounded-xl bg-zinc-800 border border-white/10 p-3 text-sm font-bold text-white focus:outline-none focus:border-yellow-500/50 appearance-none cursor-pointer hover:bg-zinc-700 transition-all !text-white !bg-zinc-800"
                        >
                          {COMMERCIAL_IMPULSES.filter(i => i.id !== 'auto').map(imp => (
                            <option key={imp.id} value={imp.id}>{imp.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex sm:block justify-end">
                      <button
                        onClick={() => {
                          console.log("[DEBUG] Deleting slot:", idx);
                          const newSched = tempSchedule.filter((_: any, i: number) => i !== idx);
                          setTempSchedule(newSched);
                        }}
                        className="w-full sm:w-auto p-3 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all flex-shrink-0 flex items-center justify-center"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    console.log("[DEBUG] Adding slot, tempSchedule:", tempSchedule);

                    const filledHours = new Array(24).fill(false);
                    for (const slot of tempSchedule) {
                      console.log("[DEBUG] Processing slot:", slot);
                      for (let h = slot.start; h < slot.end; h++) {
                        if (h >= 0 && h < 24) filledHours[h] = true;
                      }
                    }

                    console.log("[DEBUG] filledHours:", filledHours);

                    let gap = -1;
                    for (let h = 0; h < 24; h++) {
                      if (!filledHours[h]) {
                        gap = h;
                        break;
                      }
                    }

                    console.log("[DEBUG] Gap found:", gap);

                    if (gap === -1) {
                      toast("Horario completo (24h configuradas)");
                      return;
                    }

                    const start = gap;
                    const end = Math.min(24, start + 2);
                    setTempSchedule([...tempSchedule, { start: start, end: end, folder: 'active' }]);
                  }}
                  className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-white/5 py-4 text-[10px] font-bold uppercase tracking-widest text-white/60 transition-all hover:bg-white/10 hover:text-white"
                >
                  <Plus size={16} />
                  Añadir Tramo
                </button>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      handleUpdateCircadianSchedule(tempSchedule);
                    }}
                    className="rounded-2xl border border-white/10 bg-black py-4 text-[10px] font-bold uppercase tracking-widest text-white transition-all hover:bg-white/5"
                  >
                    Guardar
                  </button>
                  <button
                    onClick={() => {
                      handleUpdateCircadianSchedule(tempSchedule);
                      setShowCircadianModal(false);
                    }}
                    className="rounded-2xl bg-white py-4 text-[10px] font-bold uppercase tracking-widest text-black transition-all hover:scale-[1.02] shadow-xl shadow-white/5"
                  >
                    Guardar y Cerrar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header - Refined for Mobile */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex h-8 w-8 rounded-lg bg-gradient-to-br from-white/20 to-white/5 p-1.5 ring-1 ring-white/10">
              <ImageIcon className="h-full w-full text-white" />
            </div>
            <h1 className="text-base sm:text-lg font-medium tracking-tight truncate max-w-[150px] sm:max-w-none">
              {impersonatedUid
                ? `Gestionando: ${targetUserProfile?.email || '...'}`
                : (adminTitle || establishmentName || 'Aura Admin')}
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {(SUPER_ADMIN_EMAILS.includes(user?.email || '') || ['admin', 'Admin', 'sales', 'Comercial'].includes(userProfile?.role)) && (
              <button
                onClick={() => navigate('/admin/super')}
                className="group flex h-9 w-9 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 rounded-full bg-yellow-500/10 sm:px-4 sm:py-2 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-yellow-500 transition-all hover:bg-yellow-500 hover:text-black tv-focus shadow-[0_0_20px_rgba(234,179,8,0.1)]"
                title={['sales', 'Comercial'].includes(userProfile?.role) ? "CRM Ventas" : "Super Admin"}
              >
                <ShieldCheck size={16} />
                <span className="hidden sm:inline">{['sales', 'Comercial'].includes(userProfile?.role) ? "CRM Ventas" : "Super Admin"}</span>
              </button>
            )}
            {!isStandalone && (
              <button
                onClick={handleInstallAdmin}
                className="group flex h-9 sm:h-auto items-center justify-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-[10px] sm:text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-blue-400 font-sans shadow-lg shadow-blue-500/20"
                title="Instalar App Admin"
              >
                <Download size={16} />
                <span className="hidden sm:inline">Instalar Admin</span>
              </button>
            )}
            <button
              onClick={() => setShowManualPairing(true)}
              className="group flex h-9 w-9 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 rounded-full bg-white/10 sm:px-4 sm:py-2 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-white hover:text-black tv-focus"
              title="Vincular TV"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Vincular TV</span>
            </button>
            <button
              onClick={handleLogout}
              className="group flex h-9 w-9 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 rounded-full bg-white/5 sm:px-4 sm:py-2 text-[10px] sm:text-xs font-medium text-white/60 transition-all hover:bg-white/10 hover:text-white tv-focus"
              title="Cerrar Sesión"
            >
              <LogOut size={16} className="transition-transform group-hover:-translate-x-0.5" />
              <span className="hidden sm:inline">Cerrar</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-4 md:p-10 pb-32 md:pb-10 overflow-x-hidden">

        {/* Mobile Tab Switcher */}
        <div className="flex mb-6 p-1 bg-black/60 rounded-2xl border border-white/10 backdrop-blur-xl sticky top-[75px] z-40 shadow-2xl overflow-x-auto whitespace-nowrap scrollbar-hide">
          <button onClick={() => handleTabChange('mando')} className={`flex-none px-6 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all ${activeTab === 'mando' ? 'bg-white text-black shadow-lg shadow-white/5' : 'text-white/40'}`}>Mando</button>
          <button onClick={() => handleTabChange('audio')} className={`flex-none px-6 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all ${activeTab === 'audio' ? 'bg-white text-black shadow-lg shadow-white/5' : 'text-white/40'}`}>Audio</button>
          <button onClick={() => handleTabChange('imagen')} className={`flex-none px-6 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all ${activeTab === 'imagen' ? 'bg-white text-black shadow-lg shadow-white/5' : 'text-white/40'}`}>Imagen</button>
          {isAuraStaff && (
            <button onClick={() => handleTabChange('ia')} className={`flex-none px-6 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all ${activeTab === 'ia' ? 'bg-yellow-500 text-black shadow-lg shadow-white/5' : 'text-white/40'}`}>Intell</button>
          )}
          <button onClick={() => handleTabChange('config')} className={`flex-none px-6 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all ${activeTab === 'config' ? 'bg-white text-black shadow-lg shadow-white/5' : 'text-white/40'}`}>Ajustes</button>
        </div>

        <div className="flex flex-col gap-8">
          {/* Left: Content Management */}
          <div className="space-y-12">
            {/* Tab: AURA INTELLIGENCE (Staff Only) */}
            {isAuraStaff && (
              <div className={`${activeTab !== 'ia' ? 'hidden' : 'block'} space-y-12`}>
                <div className="p-10 rounded-[3rem] bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/10">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 rounded-2xl bg-yellow-500 flex items-center justify-center text-black">
                      <Zap size={24} />
                    </div>
                    <div>
                      <h2 className="text-3xl font-black italic text-white tracking-tighter">AURA INTELLIGENCE</h2>
                      <p className="text-sm text-yellow-500/60 font-bold uppercase tracking-widest">Estrategia y Crecimiento Interno</p>
                    </div>
                  </div>

                  <div className="grid gap-8">
                    {/* Aura Intelligence Chat */}
                    <div className="bg-black/40 rounded-3xl border border-white/10 p-6">
                      <AuraAgent isEmbedded={true} mode="support" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* IMAGEN TAB */}
            <div className={`${activeTab !== 'imagen' ? 'hidden' : 'block'} space-y-12 group/visual active:scale-[0.998] transition-transform duration-500 pt-16`}>
              {/* User Slots */}
              <div className="space-y-6">
                <div className="flex items-center justify-between p-6 rounded-3xl bg-white/[0.02] border border-white/5 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-white/50" />
                  <div className="relative z-10">
                    <h2 className="text-2xl font-semibold tracking-tight text-white mb-1">Slots de Impulso Configurables (5)</h2>
                    <p className="text-sm text-white/40">Zonas horarias e interacciones patrocinadas. Reemplazan las listas por defecto con programación específica.</p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  {[0, 1, 2, 3, 4].map((index) => {
                    const slot = merchantSlots[index] || { text: 'Disponible', active: false };
                    return (
                      <div
                        key={`merchant-${index}`}
                        className={`relative group aspect-[4/5] rounded-3xl border border-white/5 transition-all overflow-hidden p-6 flex flex-col justify-between ${slot.active ? 'bg-white/[0.05]' : 'bg-white/[0.01]'}`}
                      >
                        {slot.imageUrl && (
                          <img
                            src={slot.imageUrl}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-20"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <div className="relative z-10">
                          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Slot {index + 1}</span>
                          <h3 className="mt-2 text-xl font-bold text-white leading-tight break-words">
                            {slot.text || "Disponible"}
                          </h3>
                        </div>

                        <div className="relative z-10 flex flex-col gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingQuoteIndex(index);
                              setEditingQuoteType('merchant');
                              setNewQuote(slot.active ? slot : { text: '', category: '', price: '', tag: '', imageUrl: '', showClock: false });
                              setTimeout(() => document.getElementById('slot-editor')?.scrollIntoView({ behavior: 'smooth' }), 50);
                            }}
                            className="w-full rounded-xl bg-white text-black py-3 text-[10px] font-bold uppercase tracking-widest hover:bg-yellow-500 transition-colors shadow-lg"
                          >
                            Configurar
                          </button>
                        </div>

                        {slot.active && (
                          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-500/20 border border-blue-500/30">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                            <span className="text-[8px] font-black text-blue-500 uppercase tracking-tighter">IMPULSO</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Red Aura (Admin Mode Slots) */}
              {isAuraStaff && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-6 rounded-3xl bg-yellow-500/5 border border-yellow-500/20 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-yellow-500/80" />
                    <div className="relative z-10">
                      <h2 className="text-2xl font-semibold tracking-tight text-white italic font-serif flex items-center gap-3">
                        <Star size={24} className="text-yellow-500" />
                        Red Aura (Slots de Red)
                      </h2>
                      <p className="text-sm text-yellow-500/60 font-medium">Espacios globales que generan AuraCredits al reproducirse.</p>
                    </div>
                    <span className="px-4 py-2 rounded-full bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest shadow-[0_0_20px_rgba(234,179,8,0.3)]">Admin Mode</span>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {[0, 1, 2, 3, 4].map((index) => {
                      const slot = auraSlots[index] || { text: 'Aura Content', active: false };
                      const isLocked = !canAccessCollaborativeSlides && index >= 0; // Total clients have access to all 5

                      return (
                        <div
                          key={`aura-${index}`}
                          className={`relative group aspect-[4/5] rounded-3xl border ${isLocked ? 'border-white/5 bg-white/5' : 'border-yellow-500/20 bg-yellow-500/5'} transition-all overflow-hidden p-6 flex flex-col justify-between hover:bg-yellow-500/10`}
                        >
                          {slot.imageUrl && (
                            <img
                              src={slot.imageUrl}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-luminosity"
                              referrerPolicy="no-referrer"
                            />
                          )}
                          <div className="relative z-10">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-500/80">Slot {index + 1} {isLocked ? '(BLOQUEADO)' : ''}</span>
                            <h3 className="mt-2 text-sm font-bold text-white leading-tight line-clamp-3">
                              {isLocked ? "Contenido Premium" : (slot.text || "Contenido de Red")}
                            </h3>
                          </div>

                          <div className="relative z-10 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!isLocked && (
                              <button
                                onClick={() => {
                                  setEditingQuoteIndex(index);
                                  setEditingQuoteType('aura');
                                  setNewQuote(slot.text !== 'Aura Content' ? slot : { text: '', category: '', price: '', tag: '', imageUrl: '', showClock: false, isReferral: false });
                                  setTimeout(() => document.getElementById('slot-editor')?.scrollIntoView({ behavior: 'smooth' }), 50);
                                }}
                                className="w-full rounded-xl bg-yellow-500 text-black py-3 text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-colors shadow-lg shadow-yellow-500/20"
                              >
                                EDITAR AURA
                              </button>
                            )}
                          </div>

                          {slot.isReferral && !isLocked && (
                            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-yellow-500/20 border border-yellow-500/40 backdrop-blur-md">
                              <Share2 size={10} className="text-yellow-500" />
                              <span className="text-[8px] font-black text-yellow-500 uppercase tracking-tighter">REFERIDO</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Shared Editor for Slots */}
              {editingQuoteIndex !== null && (
                <div id="slot-editor" className="space-y-6 pt-6 border-t border-white/10 scroll-mt-32">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                      {editingQuoteType === 'merchant' ? <Monitor size={24} className="text-white" /> : <Star size={24} className="text-yellow-500" />}
                      Configuración Visual del {editingQuoteType === 'merchant' ? 'Slot de Impulso' : 'Slot de Red'} #{editingQuoteIndex + 1}
                    </h3>
                  </div>

                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (editingQuoteType === 'merchant') {
                      await saveMerchantSlot(editingQuoteIndex, newQuote);
                    } else if (editingQuoteType === 'aura') {
                      await saveAuraSlot(editingQuoteIndex, newQuote);
                    }
                    setEditingQuoteIndex(null);
                    setEditingQuoteType(null);
                    setNewQuote({ category: '', text: '', price: '', tag: '', ticker: '', imageUrl: '', showClock: false });
                  }} className={`grid gap-6 rounded-[2.5rem] border p-10 ${editingQuoteType === 'aura' ? 'border-yellow-500/20 bg-yellow-500/5' : 'border-white/10 bg-white/[0.03]'}`}>

                    <div className="mb-4 bg-white/5 p-6 rounded-2xl border border-white/10">
                      <p className="text-white/80 text-sm mb-2 font-bold">💡 ¿Cómo funciona esta configuración?</p>
                      <ul className="text-white/60 text-xs space-y-1 ml-4 list-disc">
                        <li>Los textos que introduzcas aquí aparecerán de forma elegante sobre la imagen que elijas.</li>
                        <li><strong>URL Imagen de Fondo:</strong> Esta será la imagen principal a pantalla completa para este anuncio o "slot".</li>
                        <li>Puedes escribir manualmente una URL extena o hacer clic en <strong>Copiar URL</strong> en cualquiera de las imágenes que ya hayas subido a la Galería (más abajo) para usarla automáticamente aquí.</li>
                      </ul>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Categoría</label>
                        <input
                          type="text"
                          value={newQuote.category}
                          onChange={(e) => setNewQuote({ ...newQuote, category: e.target.value })}
                          placeholder="Ej: EVENTO ESPECIAL"
                          className="w-full rounded-2xl border border-white/10 bg-black px-6 py-4 text-sm focus:border-white/50 focus:outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Título Principal</label>
                        <input
                          type="text"
                          value={newQuote.text}
                          onChange={(e) => setNewQuote({ ...newQuote, text: e.target.value })}
                          placeholder="Ej: Tardeo de Domingos"
                          className="w-full rounded-2xl border border-white/10 bg-black px-6 py-4 text-sm focus:border-white/50 focus:outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Precio / Subtítulo</label>
                        <input
                          type="text"
                          value={newQuote.price}
                          onChange={(e) => setNewQuote({ ...newQuote, price: e.target.value })}
                          placeholder="Ej: A partir de 25€"
                          className="w-full rounded-2xl border border-white/10 bg-black px-6 py-4 text-sm focus:border-white/50 focus:outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Etiqueta Inferior (Botón Call-to-action)</label>
                        <input
                          type="text"
                          value={newQuote.tag}
                          onChange={(e) => setNewQuote({ ...newQuote, tag: e.target.value })}
                          placeholder="Ej: RESERVAR AHORA"
                          className="w-full rounded-2xl border border-white/10 bg-black px-6 py-4 text-sm focus:border-white/50 focus:outline-none transition-all"
                        />
                      </div>
                      <div className="col-span-full space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">URL Imagen de Fondo (Directa o de la Galería de abajo)</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newQuote.imageUrl}
                            onChange={(e) => setNewQuote({ ...newQuote, imageUrl: e.target.value })}
                            placeholder="https://..."
                            className="flex-1 rounded-2xl border border-white/10 bg-black px-6 py-4 text-sm focus:border-white/50 focus:outline-none transition-all"
                          />
                        </div>
                        <p className="text-[10px] text-white/30 hidden sm:block">💡 Consejo: Selecciona primero una imagen de tu Galería más abajo con el botón 📺 para copiar su URL automáticamente aquí o utiliza una externa.</p>
                      </div>

                      {editingQuoteType === 'aura' && (
                        <div className="col-span-full p-4 rounded-2xl bg-yellow-500/5 border border-yellow-500/20 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${newQuote.isReferral ? 'bg-yellow-500 text-black' : 'bg-white/5 text-white/20'}`}>
                              <Share2 size={16} />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-white">Modo Referido</p>
                              <p className="text-[9px] text-white/40 uppercase tracking-[0.1em]">Genera AuraCredits para el cliente al mostrarse.</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setNewQuote({ ...newQuote, isReferral: !newQuote.isReferral })}
                            className={`px-4 py-2 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${newQuote.isReferral ? 'bg-yellow-500 text-black shadow-lg shadow-yellow-500/20' : 'bg-white/5 text-white/40'}`}
                          >
                            {newQuote.isReferral ? 'ACTIVADO' : 'DESACTIVADO'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 mt-6">
                      <button type="button" onClick={() => { setEditingQuoteIndex(null); setEditingQuoteType(null); }} className="w-full sm:w-1/3 bg-white/5 text-white/60 rounded-2xl py-4 text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-all border border-white/10">
                        CANCELAR EDICIÓN
                      </button>
                      <button type="submit" className={`w-full sm:w-2/3 flex-1 rounded-2xl py-4 text-sm font-black uppercase tracking-[0.2em] transition-all shadow-xl ${editingQuoteType === 'aura' ? 'bg-yellow-500 text-black hover:bg-white' : 'bg-white text-black hover:bg-gray-200'}`}>
                        GUARDAR CONFIGURACIÓN DEL SLOT
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="w-full h-px bg-white/10 my-10" />

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 rounded-3xl bg-white/[0.02] border border-white/5 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50" />
                <div className="relative z-10">
                  <h2 className="text-2xl font-semibold tracking-tight">Imágenes e Impulsos de Rotativa</h2>
                  <p className="text-sm text-white/40">Sube imágenes para usar como fondo en los slots, o prográmalas como Impulsos automáticos temporizados.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {contents.length > 0 && (
                    <button
                      onClick={handleDeleteAllContents}
                      className="flex items-center gap-2 rounded-xl bg-red-500/10 px-3 py-2 text-xs font-bold text-red-500 transition-all hover:bg-red-500 hover:text-white tv-focus"
                    >
                      <Trash2 size={16} />
                      <span className="hidden sm:inline">Borrar Todo</span>
                    </button>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-black transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 tv-focus"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? '...' : 'Subir'}
                  </button>

                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUpload}
                  className="hidden"
                  accept="image/*"
                />
              </div>

              <div
                className={`grid gap-4 sm:grid-cols-2 xl:grid-cols-3 transition-all duration-300 rounded-3xl p-4 ${isDragging ? 'bg-white/5 ring-2 ring-dashed ring-white/20' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <AnimatePresence mode="popLayout">
                  {contents.map((item) => (
                    <motion.div
                      key={item.createdAt}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="group relative aspect-video overflow-hidden rounded-2xl border border-white/5 bg-white/5"
                    >
                      <img
                        src={item.url}
                        alt={item.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <span className="truncate text-[10px] font-medium text-white/80">{item.name}</span>
                            {item.schedule?.enabled && <Clock size={10} className="text-green-400 flex-shrink-0" />}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(item.url);
                                  setShowToast({ show: true, message: "URL copiada al portapapeles 📋", type: "success" });
                                  if (editingQuoteIndex !== null) {
                                    setNewQuote({ ...newQuote, imageUrl: item.url });
                                    document.getElementById('slot-editor')?.scrollIntoView({ behavior: 'smooth' });
                                  }
                                } catch (err) {
                                  console.error('Error copying to clipboard component', err);
                                }
                              }}
                              className="rounded-lg bg-white/20 p-2 text-white backdrop-blur-md transition-colors hover:bg-yellow-500 hover:text-black tv-focus flex gap-1 items-center"
                              title="Copiar URL para Slot"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={() => setShowImageImpulseModal({ show: true, imagePath: item.url, duration: 15 })}
                              className="rounded-lg bg-blue-500/20 p-2 text-blue-400 backdrop-blur-md transition-colors hover:bg-blue-500 hover:text-white tv-focus flex gap-1 items-center"
                              title="Lanzar como Impulso Visual"
                            >
                              <Zap size={14} />
                              <span className="text-[10px] uppercase font-bold hidden sm:inline px-1">Impulso</span>
                            </button>
                            <button
                              onClick={() => setEditingSchedule({ type: 'content', index: contents.indexOf(item) })}
                              className="rounded-lg bg-white/20 p-2 text-white backdrop-blur-md transition-colors hover:bg-white hover:text-black tv-focus"
                            >
                              <Clock size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="rounded-lg bg-red-500/20 p-2 text-red-400 backdrop-blur-md transition-colors hover:bg-red-500 hover:text-white tv-focus"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {contents.length === 0 && !uploading && (
                  <div className="col-span-full flex h-60 flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-white/[0.02]">
                    <div className="mb-4 rounded-full bg-white/5 p-4">
                      <ImageIcon className="h-6 w-6 text-white/20" />
                    </div>
                    <p className="text-sm text-white/30 text-center px-4">No hay contenidos todavía. Empezar subiendo una imagen.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tickers Management */}
            <div className={`${activeTab !== 'imagen' ? 'hidden' : 'block'} space-y-6 group/visual active:scale-[0.998] transition-transform duration-500`}>
              {canManageTicker && (
                <div className="space-y-6 pt-10 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold tracking-tight">Mensajes del Ticker</h2>
                      <p className="text-sm text-white/40">Gestiona los mensajes que fluyen en la barra inferior (Independientes).</p>
                    </div>
                  </div>

                  <form id="ticker-form" onSubmit={handleAddTicker} className="grid gap-4 rounded-3xl border border-white/5 bg-white/[0.02] p-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Mensaje del Ticker</label>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={newTicker}
                          onChange={(e) => setNewTicker(e.target.value)}
                          placeholder="Ej: PRÓXIMA JUNTA DE VECINOS EL LUNES A LAS 19:00"
                          className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                        />
                        <button
                          type="submit"
                          disabled={!newTicker}
                          className={`rounded-xl px-8 py-3 text-sm font-bold uppercase tracking-widest transition-all disabled:opacity-30 ${editingTickerIndex !== null ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-white/10 hover:bg-white hover:text-black'}`}
                        >
                          {editingTickerIndex !== null ? <Check size={18} /> : <Plus size={18} />}
                        </button>
                        {editingTickerIndex !== null && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingTickerIndex(null);
                              setNewTicker('');
                            }}
                            className="rounded-xl bg-white/5 px-4 py-3 text-white/60 transition-all hover:bg-white/10 hover:text-white"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </form>

                  <div className="space-y-3">
                    <AnimatePresence mode="popLayout">
                      {tickers.map((ticker, idx) => (
                        <motion.div
                          key={idx}
                          layout
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className="flex items-center justify-between gap-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4 group"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-medium text-white/90 break-words line-clamp-2">{ticker.text}</p>
                              {ticker.schedule?.enabled && <Clock size={10} className="text-green-400 flex-shrink-0 mt-1" />}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditingTicker(idx)}
                              className="rounded-lg bg-white/5 p-2 text-white/40 transition-all hover:bg-white/10 hover:text-white"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => setEditingSchedule({ type: 'ticker', index: idx })}
                              className="rounded-lg bg-white/5 p-2 text-white/40 transition-all hover:bg-white/10 hover:text-white"
                            >
                              <Clock size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteTicker(ticker)}
                              className="rounded-lg bg-white/5 p-2 text-white/20 transition-all hover:bg-red-500/20 hover:text-red-400"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {tickers.length === 0 && (
                      <div className="flex h-24 flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-white/[0.02]">
                        <p className="text-xs text-white/20">No hay mensajes de ticker configurados.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mando (Control Remoto) */}
          <div className={`${activeTab !== 'mando' ? 'hidden' : 'block'} space-y-8`}>
            {/* Control Remoto (Tiempo Real) DUPLICATED FOR TAB MANDO */}
            <div className="rounded-[2rem] border border-white/5 bg-white/[0.03] p-8 backdrop-blur-sm relative overflow-hidden transition-all">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-500/50" />
              <h3 className="mb-6 text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Control Remoto (Tiempo Real)</h3>

              <div className="space-y-6">
                {/* Zen & Interfaz */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Siguiente */}
                  <button
                    onClick={async () => {
                      const clientRef = doc(db, 'clientes', targetUid);
                      try {
                        await updateClientDoc(clientRef, { nextTrackRequestedAt: serverTimestamp() });
                      } catch (err) { }
                    }}
                    className="flex flex-col items-center gap-3 p-6 rounded-2xl border bg-black border-white/10 hover:border-yellow-500/50 hover:bg-yellow-500/10 transition-all tv-focus"
                  >
                    <RefreshCw className="text-white/60" size={24} />
                    <span className="text-xs font-bold uppercase">Siguiente</span>
                  </button>
                  <button
                    onClick={() => {
                      const newVal = !isZenMode;
                      setIsZenMode(newVal);
                      const clientRef = doc(db, 'clientes', targetUid);
                      updateDoc(clientRef, { isZenMode: newVal });
                    }}
                    className={`flex flex-col items-center gap-3 p-6 rounded-2xl border transition-all tv-focus ${isZenMode ? 'bg-yellow-500/20 border-yellow-500/50' : 'bg-black border-white/10 hover:bg-white/5'}`}
                  >
                    <EyeOff className={isZenMode ? 'text-yellow-500' : 'text-white/60'} size={24} />
                    <span className="text-xs font-bold uppercase">Zen Mode</span>
                  </button>
                  <button
                    onClick={() => {
                      const newVal = !isControlsVisible;
                      setIsControlsVisible(newVal);
                      const clientRef = doc(db, 'clientes', targetUid);
                      updateDoc(clientRef, { isControlsVisible: newVal });
                    }}
                    className={`flex flex-col items-center gap-3 p-6 rounded-2xl border transition-all tv-focus ${!isControlsVisible ? 'bg-red-500/20 border-red-500/50' : 'bg-black border-white/10 hover:bg-white/5'}`}
                  >
                    <Monitor className={!isControlsVisible ? 'text-red-500' : 'text-white/60'} size={24} />
                    <span className="text-xs font-bold uppercase">Ocultar UI</span>
                  </button>
                </div>

                {/* Rendimiento & Escalado */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      const newVal = performanceMode === 'high' ? 'eco' : 'high';
                      setPerformanceMode(newVal);
                      const clientRef = doc(db, 'clientes', targetUid);
                      updateDoc(clientRef, { performanceMode: newVal });
                    }}
                    className={`flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border transition-all tv-focus ${performanceMode === 'eco' ? 'bg-green-500/20 border-green-500/50' : 'bg-black border-white/10 hover:bg-white/5'}`}
                  >
                    <Activity className={performanceMode === 'eco' ? 'text-green-500' : 'text-white/60'} size={24} />
                    <span className="text-xs font-bold uppercase">{performanceMode === 'eco' ? 'Modo: Aura Eco' : 'Modo: Aura Visual'}</span>
                  </button>
                  <div className="flex flex-col justify-center gap-4 p-6 rounded-2xl border border-white/10 bg-black">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-widest text-white/40">Escala de Textos</span>
                      <span className="text-xs font-mono font-bold text-white/60">{Math.round(centralTextScale * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={centralTextScale}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setCentralTextScale(val);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { centralTextScale: val });
                      }}
                      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white hover:accent-yellow-500 transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Audio */}
          <div className={`${activeTab !== 'audio' ? 'hidden' : 'block'} space-y-8`}>
            {/* On Air Section */}
            <OnAir clientId={targetUid} />

            {/* Favorites Section */}
            <div className="rounded-[2rem] border border-white/5 bg-white/[0.03] p-8 backdrop-blur-sm relative overflow-hidden transition-all">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-red-500/50" />
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Gestionar Favoritos</h3>
                <button onClick={() => setShowManageFavorites(!showManageFavorites)} className="text-white/40 hover:text-white transition-colors">
                  {showManageFavorites ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {showManageFavorites && (
                <div className="space-y-4">
                  {/* Central Player */}
                  {playingIndex !== null && favoriteTracks[playingIndex] && (
                    <div className="bg-white/10 p-4 rounded-xl border border-white/20 sticky top-0 z-10">
                      <p className="text-xs text-white/60 mb-2 uppercase tracking-wider">Reproduciendo ahora</p>
                      <p className="text-sm font-bold text-white mb-3">{favoriteTracks[playingIndex].title}</p>
                      <audio
                        src={favoriteTracks[playingIndex].url}
                        controls
                        autoPlay
                        onEnded={handleTrackEnded}
                        className="w-full h-8"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    {paginatedFavorites.map((track) => {
                      // Find actual index in original list to handle selection correctly
                      const index = favoriteTracks.findIndex(t => t.id === track.id);
                      return (
                        <div key={track.id} className={`p-3 rounded-lg flex items-center justify-between cursor-pointer transition-colors ${playingIndex === index ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-white/5 hover:bg-white/10'}`}>
                          <div className="flex-1" onClick={() => setPlayingIndex(index)}>
                            <span className={`text-sm ${playingIndex === index ? 'text-yellow-500 font-medium' : 'text-white/80'}`}>{track.title}</span>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); deleteFavorite(track.id, track.title); }} className="text-white/40 hover:text-red-500 transition-colors ml-4">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                    {favoriteTracks.length === 0 && <p className="text-white/30 text-xs italic">Aún no hay canciones favoritas.</p>}

                    {/* Pagination controls */}
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center gap-4 pt-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                          disabled={currentPage === 0}
                          className="p-1 rounded bg-white/10 disabled:opacity-30 text-white"
                        >
                          Anterior
                        </button>
                        <span className="text-xs text-white/50">{currentPage + 1} / {totalPages}</span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                          disabled={currentPage === totalPages - 1}
                          className="p-1 rounded bg-white/10 disabled:opacity-30 text-white"
                        >
                          Siguiente
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {(activeTab === 'audio' || activeTab === 'mando') && (
            <div className="rounded-[2rem] border border-white/5 bg-white/[0.03] p-8 backdrop-blur-sm relative overflow-hidden transition-all">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-purple-500/50" />
              <h3 className="mb-6 text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Configuración de Audio</h3>

              <div className="space-y-6">
                <div className="flex flex-col gap-6 rounded-2xl border border-white/10 bg-black p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Volume2 className="h-6 w-6 text-purple-500" />
                      <div>
                        <span className="text-sm font-bold uppercase tracking-widest text-white/80 block">Volumen Remoto</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/40">Control de audio en tiempo real</span>
                      </div>
                    </div>
                    <span className="text-xl font-mono font-bold text-white/90">
                      {Math.round(remoteVolume * 100)}%
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        const newVal = remoteVolume > 0 ? 0 : 0.4;
                        setRemoteVolume(newVal);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { remote_volume: newVal });
                      }}
                      className={`p-3 rounded-xl transition-all tv-focus ${remoteVolume === 0 ? 'bg-red-500/20 text-red-500 border border-red-500/50' : 'bg-white/5 text-white/80 hover:bg-white/10 border border-white/10'}`}
                    >
                      {remoteVolume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={remoteVolume}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setRemoteVolume(val);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { remote_volume: val });
                      }}
                      className="flex-1 h-3 bg-white/10 rounded-full appearance-none cursor-pointer accent-white hover:accent-purple-400 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowCircadianModal(true)}
                    className="flex items-center gap-4 rounded-2xl bg-white/[0.02] p-6 text-left border border-white/5 transition-all hover:bg-white/[0.05] tv-focus"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-500">
                      <Clock size={24} />
                    </div>
                    <div>
                      <span className="text-sm font-bold uppercase tracking-widest text-white/80 block">Horario Circadiano</span>
                      <span className="text-[10px] uppercase tracking-widest text-white/40">Gestionar rotación musical por defecto</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setShowImpulses(true)}
                    className="flex items-center gap-4 rounded-2xl bg-white/[0.02] p-6 text-left border border-white/5 transition-all hover:bg-white/[0.05] tv-focus group z-[200]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center text-yellow-500 group-hover:scale-110 transition-transform">
                      <Activity size={24} />
                    </div>
                    <div>
                      <span className="text-sm font-bold uppercase tracking-widest text-white/80 block">Impulsos Musicales</span>
                      <span className="text-[10px] uppercase tracking-widest text-white/40">Lanzar listas de reproducción temporales</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Right Column: Config & QR */}
        <div className={`${activeTab !== 'config' ? 'hidden' : 'block'} space-y-8`}>
          <div className="group/config rounded-[2rem] border border-white/5 bg-white/[0.03] p-8 backdrop-blur-sm relative overflow-hidden transition-all hover:bg-white/[0.04]">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-green-500/50" />
            <h3 className="mb-6 text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Configuración de Pantalla</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Nombre Establecimiento</label>
                  <InfoButton title="Nombre Establecimiento" content="El nombre que aparecerá identificando tu negocio en la plataforma y en las comunicaciones." />
                </div>
                <input
                  type="text"
                  value={establishmentName}
                  onChange={(e) => setEstablishmentName(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-black px-5 py-4 text-sm focus:border-white/20 focus:outline-none transition-all focus:ring-1 focus:ring-white/10"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Título Panel de Control</label>
                <input
                  type="text"
                  value={adminTitle}
                  onChange={(e) => setAdminTitle(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                  placeholder="Ej: Mi Negocio Admin"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Ubicación (Ciudad, País)</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                  placeholder="Huelva, ES"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">URL Personalizada (Slug)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] font-mono text-white/20">auradisplay.es/</span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-black pl-[85px] pr-4 py-3 text-sm focus:border-white/20 focus:outline-none font-mono"
                      placeholder="mi-negocio"
                    />
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${slug}`)}
                    className="p-3 bg-white/5 hover:bg-white hover:text-black rounded-xl transition-all"
                    title="Copiar URL"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => window.open(`${window.location.origin}/${slug}`, '_blank')}
                    className="p-3 bg-white/5 hover:bg-white hover:text-black rounded-xl transition-all"
                    title="Abrir en nueva pestaña"
                  >
                    <ExternalLink size={16} />
                  </button>
                </div>
                <p className="text-[8px] text-white/20 uppercase">Esta será tu dirección pública: {window.location.origin}/{slug || '...'}</p>
              </div>




              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Tema Visual</label>
                <select
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                >
                  <option value="classic">Clásico</option>
                  <option value="minimal">Minimalista</option>
                  <option value="tech">Tecnológico</option>
                  <option value="zen">Zen</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Estilo del Ticker (Inferior)</label>
                <select
                  value={tickerTheme}
                  onChange={(e) => setTickerTheme(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-sm focus:border-white/20 focus:outline-none"
                >
                  <option value="classic">Clásico (Amarillo/Negro)</option>
                  <option value="dark">Oscuro (Negro/Blanco)</option>
                  <option value="modern">Moderno (Blanco/Negro)</option>
                  <option value="gold">Premium (Dorado/Negro)</option>
                </select>
              </div>

              <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-black p-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold uppercase tracking-widest text-white/80">Rendimiento</span>
                    <span className="text-[8px] uppercase tracking-widest text-white/40">Optimización para Smart TVs</span>
                  </div>
                  <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
                    <button
                      onClick={() => {
                        const newVal = 'high';
                        setPerformanceMode(newVal);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { performanceMode: newVal });
                      }}
                      className={`px-3 py-1 text-[8px] font-bold uppercase tracking-widest rounded-md transition-all ${performanceMode === 'high' ? 'bg-white text-black font-black' : 'text-white/40 hover:text-white'}`}
                    >
                      Aura Visual
                    </button>
                    <button
                      onClick={() => {
                        const newVal = 'eco';
                        setPerformanceMode(newVal);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { performanceMode: newVal });
                      }}
                      className={`px-3 py-1 text-[8px] font-bold uppercase tracking-widest rounded-md transition-all ${performanceMode === 'eco' ? 'bg-white text-black font-black' : 'text-white/40 hover:text-white'}`}
                    >
                      Aura Eco
                    </button>
                  </div>
                </div>
                <p className="text-[9px] leading-relaxed text-white/30 uppercase tracking-tight font-medium">
                  {performanceMode === 'eco'
                    ? "MODO ECO: Imágenes desactivadas y animaciones reducidas. Los textos informativos permanecen visibles. Ahorro máximo de recursos."
                    : "AURA VISUAL: Experiencia completa con diapositivas, transiciones premium y ticker informativo."}
                </p>
              </div>

              {/* Control de Escala de Texto Remoto */}
              <div className="flex flex-col gap-4 rounded-xl border border-white/10 bg-black p-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-white uppercase tracking-wider">Escala Central</span>
                    <span className="text-[9px] text-white/40 uppercase">Ajuste de tamaño para TV</span>
                  </div>
                  <div className="flex bg-white/5 rounded-lg p-1 gap-1">
                    {[0.8, 1, 1.25, 1.5].map((s) => (
                      <button
                        key={s}
                        onClick={() => {
                          const clientRef = doc(db, 'clientes', targetUid);
                          updateDoc(clientRef, { centralTextScale: s });
                        }}
                        className={`px-3 py-1.5 rounded-md text-[9px] font-black transition-all ${centralTextScale === s
                            ? 'bg-yellow-500 text-black shadow-lg shadow-yellow-500/20'
                            : 'text-white/40 hover:text-white hover:bg-white/5'
                          }`}
                      >
                        {s === 0.8 ? 'MIN' : s === 1 ? 'DEF' : s === 1.25 ? 'MAX' : 'ULTRA'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Volumen Remoto - Temporally disabled for remote control mode to avoid sync issues */}
              {false && (
                <div className="flex flex-col gap-4 rounded-xl border border-white/10 bg-black p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-white/80">Volumen Remoto</span>
                        <Volume2 className="h-3 w-3 text-white/40" />
                      </div>
                      <span className="text-[8px] uppercase tracking-widest text-white/40">Control de audio en tiempo real</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newVal = remoteVolume > 0 ? 0 : 0.4;
                          setRemoteVolume(newVal);
                          const clientRef = doc(db, 'clientes', targetUid);
                          updateDoc(clientRef, { remote_volume: newVal });
                        }}
                        className={`p-1.5 rounded-md transition-colors ${remoteVolume === 0 ? 'bg-red-500/20 text-red-500' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                      >
                        {remoteVolume === 0 ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                      </button>
                      <span className="text-xs font-mono font-bold text-white/60 min-w-[3ch]">
                        {Math.round(remoteVolume * 100)}%
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <VolumeX className="h-4 w-4 text-white/20" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={remoteVolume}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setRemoteVolume(val);
                        // Save to CLIENT document for real-time sync with Soundscape
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { remote_volume: val });
                      }}
                      className="flex-1 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white hover:accent-green-400 transition-all"
                    />
                    <Volume2 className="h-4 w-4 text-white/40" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-widest text-white/80">Modo Zen (TV)</span>
                  <span className="text-[8px] uppercase tracking-widest text-white/40">Oculta toda la interfaz</span>
                </div>
                <button
                  onClick={() => {
                    const newVal = !isZenMode;
                    setIsZenMode(newVal);
                    const clientRef = doc(db, 'clientes', targetUid);
                    updateDoc(clientRef, { isZenMode: newVal });
                  }}
                  className={`h-6 w-11 rounded-full transition-colors relative ${isZenMode ? 'bg-yellow-500' : 'bg-white/10'}`}
                >
                  <div className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${isZenMode ? 'left-6' : 'left-1'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-widest text-white/80">Ocultar Controles (TV)</span>
                  <span className="text-[8px] uppercase tracking-widest text-white/40">Muestra/Oculta el reproductor</span>
                </div>
                <button
                  onClick={() => {
                    const newVal = !isControlsVisible;
                    setIsControlsVisible(newVal);
                    const clientRef = doc(db, 'clientes', targetUid);
                    updateDoc(clientRef, { isControlsVisible: newVal });
                  }}
                  className={`h-6 w-11 rounded-full transition-colors relative ${!isControlsVisible ? 'bg-red-500' : 'bg-white/10'}`}
                >
                  <div className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${!isControlsVisible ? 'left-6' : 'left-1'}`} />
                </button>
              </div>

              {/* AuraCredits */}
              <div className="rounded-xl border border-yellow-500/20 bg-black/50 p-4 flex flex-col gap-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-500">AuraCredits</span>
                  <span className="text-xl font-mono text-white">{Math.floor(auraCredits)} / 100</span>
                </div>
                {/* Progress Bar */}
                <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-600 to-yellow-400 transition-all duration-1000 ease-out"
                    style={{ width: `${Math.min(100, Math.max(0, auraCredits))}%` }}
                  />
                </div>

                <div className="flex flex-col mt-2 pt-3 border-t border-white/5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1">
                    Tu Suscripción
                  </span>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white/90 capitalize">
                      Plan {targetUserProfile?.planLevel || 'Aura'}
                    </span>
                    <span className="text-xs text-white/60">
                      {targetUserProfile?.planLevel === 'pro' ? '39,90€' :
                        targetUserProfile?.planLevel === 'connect' ? '29,90€' :
                          targetUserProfile?.planLevel === 'total' ? '59,90€' : 'Cuota mensual'}
                    </span>
                  </div>

                  {auraCredits >= 100 ? (
                    <div className="mt-3 p-2 rounded bg-green-500/20 border border-green-500/30 flex text-center flex-col gap-1 items-center justify-center">
                      <span className="text-xs font-bold text-green-400">¡Suscripción 100% Gratuita Alcanzada!</span>
                      <span className="text-[9px] text-green-400/70">Tu próxima cuota será coste de 0€.</span>
                    </div>
                  ) : (
                    <span className="text-[9px] text-white/40 mt-2 leading-relaxed">
                      Sigue recibiendo ingresos de referidos. Al llegar a 100 AuraCredits, tu próxima cuota mensual te saldrá <strong className="text-yellow-500">Totalmente Gratis</strong>. ¡Sigue así!
                    </span>
                  )}
                </div>
              </div>

              {/* Control Remoto (Tiempo Real) - PRIORIDAD 1 */}
              <div className="rounded-xl border border-white/10 bg-black/50 p-6 space-y-6">
                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">Control Remoto (Tiempo Real)</h4>

                {/* Volumen Remoto */}
                <div className="flex flex-col gap-4 rounded-xl border border-white/10 bg-black p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-widest text-white/80">Volumen Remoto</span>
                      <Volume2 className="h-3 w-3 text-white/40" />
                    </div>
                    <span className="text-xs font-mono font-bold text-white/60 min-w-[3ch]">
                      {Math.round(remoteVolume * 100)}%
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <VolumeX className="h-4 w-4 text-white/20" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={remoteVolume}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setRemoteVolume(val);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { remote_volume: val });
                      }}
                      className="flex-1 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white hover:accent-green-400 transition-all"
                    />
                    <Volume2 className="h-4 w-4 text-white/40" />
                  </div>
                </div>

                {/* Zen & Interfaz */}
                <div className="grid grid-cols-3 gap-4">
                  {/* Siguiente */}
                  <button
                    onClick={async () => {
                      console.log("[AdminDashboard] Siguiente clicked for:", targetUid);
                      const clientRef = doc(db, 'clientes', targetUid);
                      try {
                        // 1. Trigger skip in AuraSoundscape
                        await updateDoc(clientRef, { nextTrackRequestedAt: serverTimestamp() });
                        console.log("[AdminDashboard] Skip write successful");
                      } catch (err) {
                        console.error("[AdminDashboard] Skip write failed:", err);
                      }
                    }}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border bg-black border-white/10 hover:border-yellow-500/50 hover:bg-yellow-500/10 transition-all"
                  >
                    <RefreshCw className="text-white/40" />
                    <span className="text-[10px] font-bold uppercase">Siguiente</span>
                  </button>
                  <button
                    onClick={() => {
                      const newVal = !isZenMode;
                      setIsZenMode(newVal);
                      const clientRef = doc(db, 'clientes', targetUid);
                      setDoc(clientRef, { isZenMode: newVal }, { merge: true });
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${isZenMode ? 'bg-yellow-500/20 border-yellow-500/50' : 'bg-black border-white/10'}`}
                  >
                    <EyeOff className={isZenMode ? 'text-yellow-500' : 'text-white/40'} />
                    <span className="text-[10px] font-bold uppercase">Zen</span>
                  </button>
                  <button
                    onClick={() => {
                      const newVal = !isControlsVisible;
                      setIsControlsVisible(newVal);
                      const clientRef = doc(db, 'clientes', targetUid);
                      updateDoc(clientRef, { isControlsVisible: newVal });
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${!isControlsVisible ? 'bg-red-500/20 border-red-500/50' : 'bg-black border-white/10'}`}
                  >
                    <Monitor className={!isControlsVisible ? 'text-red-500' : 'text-white/40'} />
                    <span className="text-[10px] font-bold uppercase">Interfaz</span>
                  </button>
                </div>

                {/* Favorite Audio Tracks */}
                <div className="rounded-xl border border-red-500/20 bg-black/50 p-6 space-y-4">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-500">Lista de Favoritos (Audio)</h4>
                  <div className="space-y-2">
                    {favoriteTracks.slice(0, 5).map(f => (
                      <div key={f.id} className="flex items-center gap-2 text-[10px] bg-white/5 p-2 rounded">
                        <div className="w-1 h-1 rounded-full bg-red-500" />
                        <span className="truncate">{f.title}</span>
                      </div>
                    ))}
                    {favoriteTracks.length === 0 && <p className="text-[10px] text-white/30">Sin favoritos aún.</p>}
                  </div>
                </div>

                {/* Rendimiento & Escalado (AHORA REAL-TIME) */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Performance Mode */}
                  <button
                    onClick={() => {
                      const newVal = performanceMode === 'high' ? 'eco' : 'high';
                      setPerformanceMode(newVal);
                      const clientRef = doc(db, 'clientes', targetUid);
                      updateDoc(clientRef, { performanceMode: newVal });
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${performanceMode === 'eco' ? 'bg-green-500/20 border-green-500/50' : 'bg-black border-white/10'}`}
                  >
                    <Activity className={performanceMode === 'eco' ? 'text-green-500' : 'text-white/40'} />
                    <span className="text-[10px] font-bold uppercase">{performanceMode === 'eco' ? 'Aura Eco' : 'Aura Visual'}</span>
                  </button>
                  {/* Escala Central */}
                  <div className="flex flex-col gap-2 p-4 rounded-xl border border-white/10 bg-black">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Escala Central</span>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={centralTextScale}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setCentralTextScale(val);
                        const clientRef = doc(db, 'clientes', targetUid);
                        updateDoc(clientRef, { centralTextScale: val });
                      }}
                      className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
                    />
                  </div>
                </div>
              </div>

              {/* Configuración de Sistema (Guardado) */}
              <div className="rounded-xl border border-white/10 bg-black/50 p-6 space-y-6">
                {/* ... Inputs de configuración ... */}
              </div>

              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-widest text-white/80">Mostrar Ticker</span>
                  <span className="text-[8px] uppercase tracking-widest text-white/40">Barra de texto inferior</span>
                </div>
                <button
                  onClick={() => {
                    const newVal = !showTicker;
                    setShowTicker(newVal);
                    const clientRef = doc(db, 'clientes', targetUid);
                    updateDoc(clientRef, { showTicker: newVal });
                  }}
                  className={`h-6 w-12 rounded-full transition-colors ${showTicker ? 'bg-green-500' : 'bg-white/10'}`}
                >
                  <div className={`h-4 w-4 rounded-full bg-white transition-transform ${showTicker ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>


              {/* Aura Agent Settings */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold uppercase tracking-widest text-white/80">Aura Digital Pass</span>
                    <span className="text-[8px] uppercase tracking-widest text-white/40">Chat inteligente de ventas</span>
                  </div>
                  <button
                    onClick={() => setAuraAgentEnabled(!auraAgentEnabled)}
                    className={`h-6 w-12 rounded-full transition-colors ${auraAgentEnabled ? 'bg-green-500' : 'bg-white/10'}`}
                  >
                    <div className={`h-4 w-4 rounded-full bg-white transition-transform ${auraAgentEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>

                {auraAgentEnabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-2 pt-2 border-t border-white/10"
                  >
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">WhatsApp del Comercial (sin +)</label>
                    <input
                      type="text"
                      value={auraAgentWhatsApp}
                      onChange={(e) => setAuraAgentWhatsApp(e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm focus:border-white/20 focus:outline-none"
                      placeholder="34600000000"
                    />
                    <p className="text-[8px] text-white/20 uppercase">Ejemplo: 34664246898</p>
                  </motion.div>
                )}
              </div>

              <button
                onClick={handleUpdateConfig}
                className="w-full rounded-xl bg-white py-3 text-[10px] font-bold uppercase tracking-widest text-black transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Guardar Configuración
              </button>
            </div>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm relative overflow-hidden group/qr">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-white/20" />
            <h3 className="mb-6 text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
              {targetUserProfile?.role === 'sales' ? 'Tu Hub de Ventas' : 'Acceso Público'}
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center space-y-4 rounded-2xl border border-white/10 bg-black p-4" ref={qrRef}>
                <QRCodeCanvas
                  value={displayUrl}
                  size={160}
                  level="H"
                  includeMargin={true}
                  className="rounded-xl"
                />
                <p className="text-[10px] text-white/50 text-center">Escanea para abrir en cualquier dispositivo</p>
                <div className="flex items-center gap-2 w-full">
                  <button
                    onClick={handleDownloadQR}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-white/5 py-2 text-[10px] uppercase font-bold tracking-widest hover:bg-white hover:text-black transition-all"
                  >
                    <Download size={12} /> Descargar
                  </button>
                  <button
                    onClick={handleShareQR}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-green-500/10 py-2 text-[10px] uppercase font-bold tracking-widest text-green-500 hover:bg-green-500 hover:text-white transition-all"
                  >
                    <Share2 size={12} /> Compartir
                  </button>
                </div>
              </div>
              <div className="group relative rounded-2xl border border-white/10 bg-black p-4 transition-colors hover:border-white/20">
                <p className="break-all text-xs font-mono text-white/60">{displayUrl}</p>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={copyUrl}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white/5 py-2.5 text-[10px] font-bold uppercase tracking-widest transition-all hover:bg-white/10"
                  >
                    {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                    {copied ? 'Copiado' : 'Copiar URL'}
                  </button>
                  <a
                    href={displayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 transition-all hover:bg-white/10"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
              <div className="rounded-2xl bg-white/5 p-4 text-[10px] leading-relaxed text-white/40">
                <p>Copia esta URL y ábrela en el navegador de tu Smart TV o pantalla profesional para empezar la rotación.</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/5 bg-white/[0.03] p-8">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-white/40">Documentación Aura Business</h3>
            <button
              onClick={downloadSalesKit}
              className="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-4 text-[10px] font-bold uppercase tracking-[0.2em] transition-all hover:bg-white hover:text-black group"
            >
              <Download size={16} className="text-white/40 group-hover:text-black/40" />
              Descargar Dossier de Rentabilidad y Prestaciones (PDF)
              <Download size={14} className="ml-auto text-white/20 group-hover:text-black/20" />
            </button>
            <button
              onClick={() => window.open('/negocio', '_blank')}
              className="flex w-full items-center justify-center gap-3 rounded-2xl border border-yellow-500/30 bg-yellow-500/5 py-4 text-[10px] font-bold uppercase tracking-[0.2em] transition-all hover:bg-yellow-500 hover:text-black group mt-4"
            >
              <FileText size={16} className="text-yellow-500 group-hover:text-black/60" />
              Ver Landing de Negocio (Abierta)
              <ExternalLink size={14} className="ml-auto text-yellow-500 group-hover:text-black/40" />
            </button>
            <p className="mt-4 text-[9px] leading-relaxed text-white/30 uppercase tracking-widest">
              Documento oficial con las características técnicas y posibilidades del ecosistema.
            </p>
          </div>

          <div className="rounded-3xl border border-white/5 bg-white/[0.03] p-8">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-white/40">Estado del Sistema</h3>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 animate-pulse rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
              <span className="text-[10px] font-medium tracking-widest uppercase text-white/60">Sincronizado en Tiempo Real</span>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {editingSchedule && (
          <ScheduleModal
            item={
              editingSchedule.type === 'content' ? contents[editingSchedule.index] :
                editingSchedule.type === 'quote' ? quotes[editingSchedule.index] :
                  tickers[editingSchedule.index]
            }
            onSave={handleUpdateSchedule}
            onClose={() => setEditingSchedule(null)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAdminInstallHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 p-6 backdrop-blur-xl"
            onClick={() => setShowAdminInstallHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md rounded-[2.5rem] border border-white/10 bg-black/60 p-8 text-center shadow-2xl space-y-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center">
                <div className="relative h-16 w-16 rounded-2xl bg-white p-3 shadow-xl">
                  <img
                    src="https://solonet.es/wp-content/uploads/2026/03/LOGO-AURA-BUSINESS-512-x-512-px.png"
                    alt="Aura Admin"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
              <h3 className="font-serif text-2xl italic tracking-tight text-white mb-2 italic">Instalar Aura Admin</h3>
              <p className="text-sm text-white/60 leading-relaxed mb-4">
                Añade el <span className="text-white font-bold">Panel de Control</span> a tu pantalla de inicio para gestionar tu negocio de forma rápida y directa.
              </p>

              <div className="space-y-4 text-left bg-white/5 p-6 rounded-3xl border border-white/5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Instrucciones</p>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-[10px] font-bold">1</div>
                  <p className="text-xs text-white/80">Pulsa <span className="font-bold text-blue-400">Compartir</span> o el icono de <span className="font-bold text-white">instalar</span> en tu navegador.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-[10px] font-bold">2</div>
                  <p className="text-xs text-white/80">Selecciona <span className="font-bold text-white">"Añadir a la pantalla de inicio"</span>.</p>
                </div>
              </div>

              <div className="pt-4">
                {deferredPrompt ? (
                  <button
                    onClick={() => {
                      deferredPrompt.prompt();
                      deferredPrompt.userChoice.then(() => setShowAdminInstallHelp(false));
                    }}
                    className="w-full py-4 rounded-full bg-yellow-400 text-black text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-transform shadow-lg shadow-yellow-500/20"
                  >
                    Instalar Panel de Control
                  </button>
                ) : isStandalone ? (
                  <div className="space-y-4">
                    <p className="text-[10px] text-yellow-400 font-black uppercase tracking-tighter bg-yellow-400/10 py-2 rounded-lg">Estás dentro de la App del Display</p>
                    <p className="text-xs text-white/80">Para instalar el <span className="text-white font-bold">Panel</span> por separado, abre <span className="text-white font-mono bg-white/10 px-1 rounded">auradisplay.es/admin</span> directamente en <span className="text-white font-bold">Chrome</span> o tu navegador.</p>
                    <button
                      onClick={() => setShowAdminInstallHelp(false)}
                      className="w-full py-3 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest"
                    >
                      Lo entiendo
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAdminInstallHelp(false)}
                    className="w-full py-4 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-white/20 transition-all font-black"
                  >
                    Cerrar instrucciones
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AuraAgent mode="tutor" />
    </div>
  );
}
